import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { BannerBeneficioReferido, useBeneficioReferido } from '../components/bodas/BeneficioCotizacion';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  FileText,
  Copy,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Calculator,
  User,
  Phone,
  Mail,
  Calendar,
  Package,
  ShoppingBag,
  Wrench,
  List,
  Eye,
  X,
  Loader2,
  CreditCard,
  CalendarDays,
  AlertTriangle,
  MessageCircle,
  Award,
  Star,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DESCUENTOS_GENERALES = [0, 5, 10, 15, 20];

// Configuración BASE de monedas (los valores de tasaCOP se actualizan dinámicamente)
const MONEDAS_BASE = {
  COP: { 
    codigo: 'COP', 
    nombre: 'Peso Colombiano', 
    simbolo: '$', 
    locale: 'es-CO',
    bandera: '🇨🇴',
    decimales: 0,
    tasaCOP: 1
  },
  MXN: { 
    codigo: 'MXN', 
    nombre: 'Peso Mexicano', 
    simbolo: 'MX$', 
    locale: 'es-MX',
    bandera: '🇲🇽',
    decimales: 2,
    tasaCOP: 202
  },
  USD: { 
    codigo: 'USD', 
    nombre: 'Dólar Estadounidense', 
    simbolo: 'US$', 
    locale: 'en-US',
    bandera: '🇺🇸',
    decimales: 2,
    tasaCOP: 4200
  },
  EUR: { 
    codigo: 'EUR', 
    nombre: 'Euro', 
    simbolo: '€', 
    locale: 'de-DE',
    bandera: '🇪🇺',
    decimales: 2,
    tasaCOP: 4500
  }
};

// Variable global para las tasas actuales (se actualiza dinámicamente)
let MONEDAS = { ...MONEDAS_BASE };

// CONSTANTE: Mínimo de primera cuota SIEMPRE en COP
// Para usuarios normales: $1.000.000 COP
// Para Admin/Gerencia: pueden usar entre $500.000 y $1.000.000 COP
const MINIMO_CUOTA_COP_DEFAULT = 1000000; // $1.000.000 COP (usuarios normales)
const MINIMO_CUOTA_COP_PRIVILEGIADO = 500000; // $500.000 COP (admin/gerencia)
const MAXIMO_CUOTA_COP_PRIVILEGIADO = 1000000; // $1.000.000 COP (límite superior para admin/gerencia)

// Función para obtener el mínimo según el rol del usuario
const getMinimoSegunRol = (userRole) => {
  const rolesPrivilegiados = ['admin', 'gerencia'];
  return rolesPrivilegiados.includes(userRole) ? MINIMO_CUOTA_COP_PRIVILEGIADO : MINIMO_CUOTA_COP_DEFAULT;
};

// Función para verificar si el usuario puede usar el rango flexible
const puedeUsarRangoFlexible = (userRole) => {
  const rolesPrivilegiados = ['admin', 'gerencia'];
  return rolesPrivilegiados.includes(userRole);
};

// Función para convertir un valor de cualquier moneda a COP
// valorEnMoneda * tasaCOP = valorEnCOP
const convertirACOP = (valorEnMoneda, moneda = 'COP') => {
  const config = MONEDAS[moneda] || MONEDAS.COP;
  return Math.round(valorEnMoneda * config.tasaCOP);
};

// Función para convertir de COP a otra moneda
// valorEnCOP / tasaCOP = valorEnMoneda
const convertirDeCOP = (valorEnCOP, moneda = 'COP') => {
  const config = MONEDAS[moneda] || MONEDAS.COP;
  if (config.decimales === 0) {
    return Math.round(valorEnCOP / config.tasaCOP);
  }
  return Number((valorEnCOP / config.tasaCOP).toFixed(config.decimales));
};

// Función para obtener el mínimo de cuota expresado en la moneda seleccionada
// Para admin/gerencia: $500.000 COP, para otros: $1.000.000 COP
// Ejemplo: Si moneda = MXN y tasaCOP = 202, entonces:
// 500.000 COP / 202 = 2.475,25 MXN (redondeado según decimales)
const getMinimoEnMoneda = (moneda = 'COP', userRole = null) => {
  const minimoCOP = getMinimoSegunRol(userRole);
  return convertirDeCOP(minimoCOP, moneda);
};

// Función para obtener el máximo del rango (para admin/gerencia)
const getMaximoEnMoneda = (moneda = 'COP') => {
  return convertirDeCOP(MAXIMO_CUOTA_COP_PRIVILEGIADO, moneda);
};

// Formatear precio según la moneda
const formatPrice = (value, moneda = 'COP') => {
  if (!value && value !== 0) return `${MONEDAS[moneda]?.simbolo || '$'} 0`;
  const config = MONEDAS[moneda] || MONEDAS.COP;
  const valorFormateado = Number(value).toLocaleString(config.locale, {
    minimumFractionDigits: config.decimales,
    maximumFractionDigits: config.decimales
  });
  return `${config.simbolo} ${valorFormateado}`;
};

// Labels para tipos de prenda - PAÑOS 2026
const TIPO_PRENDA_LABELS = {
  chaleco: 'Chaleco',
  pantalon: 'Pantalón',
  blazer: 'Blazer',
  traje_invitado: 'Traje Invitado',
  smoking: 'Smoking',
  traje_novio: 'Traje Novio'
};

// ============================================================
// MAPA DE CONFECCIONES: Código interno → Nombre descriptivo
// El código interno (Traje A, Traje B, etc.) se mantiene para BD/tarifas
// El nombre descriptivo es lo que ve el usuario en el selector
// ============================================================
const CONFECCION_LABELS = {
  // TRAJES - Combinaciones de pantalón + blazer
  'Traje A': 'Traje – Pantalón sencillo + Blazer sencillo',
  'Traje B': 'Traje – Pantalón italiano + Blazer sencillo',
  'Traje C': 'Traje – Pantalón italiano + Blazer con solapa en satín removible',
  'Traje D': 'Traje – Pantalón sencillo + Blazer con solapa en satín removible',
  'Traje E': 'Traje – Pantalón italiano + Blazer diseño especial (pasapunto o bolsillo golf)',
  'Traje F': 'Traje – Diseño especial fuera de línea estándar',
  // PRENDAS INDIVIDUALES
  'Pantalón sencillo': 'Pantalón sencillo',
  'Pantalón italiano': 'Pantalón italiano',
  'Blazer sencillo': 'Blazer sencillo',
  'Blazer satín removible': 'Blazer con solapa en satín removible',
  'Chaleco': 'Chaleco (sencillo, cruzado, con solapa o botones forrados)',
  // CAMISERÍA
  'Camisa': 'Camisa'
};

// Función helper para obtener el label descriptivo de una confección
const getConfeccionLabel = (codigo) => {
  return CONFECCION_LABELS[codigo] || codigo;
};

// Tipos de item
const TIPOS_ITEM = {
  cotizador: { label: 'Producto del Cotizador', icon: List, color: 'bg-blue-100 text-blue-700' },
  manual: { label: 'Producto / Accesorio', icon: ShoppingBag, color: 'bg-purple-100 text-purple-700' },
  servicio: { label: 'Servicio Adicional', icon: Wrench, color: 'bg-green-100 text-green-700' }
};

// Estados de cotización
const ESTADOS = {
  borrador: { label: 'Borrador', color: 'bg-zinc-100 text-zinc-700', icon: FileText },
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-700', icon: Send },
  aprobada: { label: 'Aprobada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
  vencida: { label: 'Vencida', color: 'bg-orange-100 text-orange-700', icon: Clock }
};

// CONSTANTES PARA REGLAS DE PLAN DE PAGOS (fuera del componente para evitar re-renders)
// Nota: Mínimo de cuota ahora es dinámico según el rol (ver getMinimoSegunRol)
// Admin/Gerencia: $500.000 - $1.000.000 COP
// Otros usuarios: $1.000.000 COP fijo
const DIAS_LIMITE = 60; // 60 días antes del evento debe estar el 70%
const MAX_CUOTAS_70 = 6; // Máximo 6 cuotas para el 70%
const PORCENTAJE_ANTICIPO = 70; // 70% debe estar pagado antes del límite
const PORCENTAJE_SALDO = 30; // 30% se paga contra entrega

const QuoteForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtener el usuario para verificar rol
  const userRole = user?.role;
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({ 
    // Nuevo catálogo (desde /api/cotizador/catalogo)
    tipos: [],
    confecciones_por_tipo: {},
    referencias_por_tipo_confeccion: {},
    tallas_por_combinacion: {},
    express_por_combinacion: {},
    tarifas: [],
    // Legacy (desde /api/quotes/catalog)
    marcas: [], 
    colecciones_por_marca: {}, 
    tipos_prenda: [], 
    catalog: [],
    accesorios_sugeridos: [],
    servicios_sugeridos: [],
    usando_legacy: false
  });
  
  const [quote, setQuote] = useState({
    cliente_nombre: '',
    cliente_telefono: '',
    cliente_correo: '',
    // Campos del módulo de clientes
    cliente_id: '',
    cliente_tipo_documento: 'CC',
    cliente_documento: '',
    asesor_comercial: '',
    estado: 'borrador',
    vigencia_dias: 30,
    observaciones_internas: '',
    observaciones_cliente: '',
    items: [],
    descuento_porcentaje: 0,  // Porcentaje: 0, 5, 10, 15, 20
    // Plan de pagos
    fecha_evento: '',
    metodo_pago: 'normal',  // "normal" o "plan_pagos"
    porcentaje_anticipo: 70,
    numero_pagos: 3,
    plan_pagos: [],
    anticipo_previo: 0, // Pago manual previo realizado por el cliente
    // Modo de plan de pagos (solo admin/gerencia pueden elegir)
    modo_plan_pagos: '', // 'automatico' | 'manual' | 'manual_completo' - vacío significa no seleccionado
    valor_primera_cuota: 0, // Valor personalizado de la primera cuota (modo manual)
    cuotas_manuales: [], // Array de cuotas definidas manualmente (modo manual_completo)
    // Vendedor responsable
    vendedor_id: '',
    vendedor_codigo: '',
    vendedor_nombre: '',
    // Moneda de la cotización
    moneda: 'COP',
    // Puntos JPM
    puntos_jpm_a_redimir: 0,
    puntos_jpm_descuento: 0
  });

  // Beneficio de invitado/referido (10% en su primera cotización)
  const beneficioRef = useBeneficioReferido({ habilitado: !isEditing, quote, setQuote });

  // Estados para búsqueda de cliente existente
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  
  // Estados para Puntos JPM del cliente
  const [clientePuntos, setClientePuntos] = useState(null);
  const [showPuntosModal, setShowPuntosModal] = useState(false);
  const [puntosARedimir, setPuntosARedimir] = useState(0);

  // Lista de vendedores activos
  const [vendedoresActivos, setVendedoresActivos] = useState([]);

  // Estado para nuevo item
  const [tipoItem, setTipoItem] = useState('cotizador');
  
  // Estado para "Producto del Cotizador" (nuevo modelo con cascada)
  const [newItemCotizador, setNewItemCotizador] = useState({
    tipo: '',           // PAÑO, CAMISERÍA
    confeccion: '',     // Traje A, Camisa, etc.
    pano_referencia: '',// PAÑO / REFERENCIA (valor exacto de la matriz)
    talla: '',          // S, M, L, XL, A LA MEDIDA, INVITADO
    express: '',        // NO, SÍ
    cantidad: 1,
    descripcion_manual: '',   // Descripción que aparece en el PDF
    notas_internas: '',       // Notas internas (NO aparecen en PDF del cliente)
    descuento_porcentaje: 0,  // Descuento del artículo (0-100%)
    precio_base: 0            // Precio obtenido del Cotizador (solo lectura)
  });
  
  // Estado legacy para "Producto Lista de Precios" (solo si no hay tarifas importadas)
  const [newItemListaPrecios, setNewItemListaPrecios] = useState({
    marca: '',
    coleccion: '',
    referencia: '',
    tipo_prenda: '',
    cantidad: 1,
    descripcion_manual: '',  // Descripción que aparece en el PDF
    notas_internas: '',  // Notas internas (NO aparecen en PDF del cliente)
    descuento_porcentaje: 0  // Descuento del artículo (0-100%)
  });
  const [newItemManual, setNewItemManual] = useState({
    nombre: '',
    descripcion_manual: '',  // Descripción que aparece en el PDF
    precio_base: 0,
    cantidad: 1,
    referencia_material: '',  // Referencia de material/tela para items manuales
    notas_internas: '',  // Notas internas (NO aparecen en PDF del cliente)
    descuento_porcentaje: 0  // Descuento del artículo (0-100%)
  });
  const [newItemServicio, setNewItemServicio] = useState({
    nombre: '',
    descripcion_manual: '',  // Descripción que aparece en el PDF
    precio_base: 0,
    cantidad: 1,
    notas_internas: '',  // Notas internas (NO aparecen en PDF del cliente)
    descuento_porcentaje: 0  // Descuento del artículo (0-100%)
  });

  // Estado para vista previa del PDF
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Estado para tasas de cambio dinámicas
  const [tasasCargadas, setTasasCargadas] = useState(false);
  
  // Estado para límite de descuento según rol
  const [descuentoConfig, setDescuentoConfig] = useState({
    descuento_maximo_porcentaje: 20,  // Valor por defecto
    es_gerencia: false,
    puede_100_porciento: false
  });
  
  // Cargar configuración de descuento máximo según rol del usuario
  useEffect(() => {
    const fetchDescuentoConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/quotes/config/descuento-maximo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDescuentoConfig(response.data);
      } catch (error) {
        console.warn('No se pudo cargar configuración de descuento, usando valores por defecto');
      }
    };
    fetchDescuentoConfig();
  }, []);

  // Cargar tasas de cambio dinámicas
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/exchange-rates`);
        if (response.data?.tasas) {
          // Actualizar MONEDAS con las tasas dinámicas
          Object.keys(response.data.tasas).forEach(codigo => {
            if (MONEDAS[codigo]) {
              MONEDAS[codigo] = {
                ...MONEDAS[codigo],
                tasaCOP: response.data.tasas[codigo].tasa_cop || MONEDAS[codigo].tasaCOP
              };
            }
          });
          setTasasCargadas(true);
        }
      } catch (error) {
        console.warn('No se pudieron cargar las tasas de cambio dinámicas, usando valores por defecto');
        setTasasCargadas(true); // Continuar con valores por defecto
      }
    };
    fetchExchangeRates();
  }, []);

  // Cargar catálogo desde el Cotizador
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const token = localStorage.getItem('token');
        // Primero intentar cargar del nuevo endpoint del Cotizador
        const response = await axios.get(`${API_URL}/api/cotizador/catalogo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCatalog(response.data);
      } catch (error) {
        console.error('Error cargando catálogo del Cotizador:', error);
        // Fallback al endpoint legacy si falla
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/quotes/catalog`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCatalog({ ...response.data, usando_legacy: true });
        } catch (legacyError) {
          toast.error('Error al cargar el catálogo de precios');
        }
      }
    };
    fetchCatalog();
  }, []);

  // Cargar cotización existente
  useEffect(() => {
    if (isEditing) {
      const fetchQuote = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/quotes/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Normalizar datos para compatibilidad con cotizaciones antiguas
          const data = response.data;
          // Si tiene descuento antiguo (monto fijo), convertir a 0% de porcentaje
          if (data.descuento !== undefined && data.descuento_porcentaje === undefined) {
            data.descuento_porcentaje = 0;
          }
          setQuote(data);
        } catch (error) {
          console.error('Error al cargar cotización:', error.response?.data || error.message);
          const errorMsg = error.response?.data?.detail || error.response?.status || error.message;
          toast.error(`Error al cargar la cotización: ${errorMsg}`);
          navigate('/quotes');
        } finally {
          setLoading(false);
        }
      };
      fetchQuote();
    }
  }, [id, isEditing, navigate]);

  // Cargar vendedores activos
  useEffect(() => {
    const fetchVendedores = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/vendedores/activos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVendedoresActivos(response.data || []);
      } catch (error) {
        console.error('Error al cargar vendedores:', error);
      }
    };
    fetchVendedores();
  }, []);

  // Actualizar precio automáticamente cuando cambia la combinación del Cotizador
  // OPTIMIZADO: Usa mapa de precios compacto (catalog.precios) con separador |||
  useEffect(() => {
    const { tipo, confeccion, pano_referencia, talla, express } = newItemCotizador;
    if (tipo && confeccion && pano_referencia && talla && express && catalog.precios) {
      const clave = `${tipo}|||${confeccion}|||${pano_referencia}|||${talla}|||${express}`;
      const precio = catalog.precios[clave] || 0;
      if (precio !== newItemCotizador.precio_base) {
        setNewItemCotizador(prev => ({ ...prev, precio_base: precio }));
      }
    } else if (newItemCotizador.precio_base !== 0) {
      setNewItemCotizador(prev => ({ ...prev, precio_base: 0 }));
    }
  }, [newItemCotizador.tipo, newItemCotizador.confeccion, newItemCotizador.pano_referencia, newItemCotizador.talla, newItemCotizador.express, catalog.precios]);

  // Función para buscar cliente por documento, correo o teléfono
  const buscarClientePorDocumento = useCallback(async (tipoDoc, numDoc) => {
    if (!tipoDoc || !numDoc || numDoc.length < 5) {
      setClienteEncontrado(null);
      return;
    }
    
    // Si ya está asociado, no buscar
    if (quote.cliente_id) {
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clientes/validar-duplicado`, {
        params: { documento: numDoc },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.encontrado) {
        setClienteEncontrado(response.data);
        setShowClienteModal(true);
      } else {
        setClienteEncontrado(null);
      }
    } catch (error) {
      console.error('Error buscando cliente:', error);
      setClienteEncontrado(null);
    } finally {
      setBuscandoCliente(false);
    }
  }, [quote.cliente_id]);

  // Buscar cliente por correo
  const buscarClientePorCorreo = useCallback(async (correo) => {
    if (!correo || !correo.includes('@') || correo.length < 5) {
      return;
    }
    
    // Si ya está asociado, no buscar
    if (quote.cliente_id) {
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clientes/validar-duplicado`, {
        params: { correo },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.encontrado) {
        setClienteEncontrado(response.data);
        setShowClienteModal(true);
      }
    } catch (error) {
      console.error('Error buscando cliente por correo:', error);
    } finally {
      setBuscandoCliente(false);
    }
  }, [quote.cliente_id]);

  // Buscar cliente por teléfono
  const buscarClientePorTelefono = useCallback(async (telefono) => {
    const telLimpio = (telefono || '').replace(/\D/g, '');
    if (telLimpio.length < 7) {
      return;
    }
    
    // Si ya está asociado, no buscar
    if (quote.cliente_id) {
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clientes/validar-duplicado`, {
        params: { telefono },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.encontrado) {
        setClienteEncontrado(response.data);
        setShowClienteModal(true);
      }
    } catch (error) {
      console.error('Error buscando cliente por teléfono:', error);
    } finally {
      setBuscandoCliente(false);
    }
  }, [quote.cliente_id]);

  // Asociar cliente existente a la cotización con todos sus datos
  const asociarClienteExistente = async () => {
    if (clienteEncontrado?.cliente) {
      const cliente = clienteEncontrado.cliente;
      const ultimaFicha = clienteEncontrado.ultima_ficha;
      const ultimaCot = clienteEncontrado.ultima_cotizacion;
      
      const clienteIdDisplay = cliente.id_cliente || cliente.id;
      const clienteIdUUID = cliente.id; // UUID real para consultas de puntos
      
      setQuote(prev => ({
        ...prev,
        cliente_id: clienteIdDisplay,
        cliente_uuid: clienteIdUUID, // Guardar UUID para puntos
        cliente_nombre: cliente.nombre,
        cliente_telefono: cliente.telefono || prev.cliente_telefono,
        cliente_correo: cliente.correo || prev.cliente_correo,
        cliente_tipo_documento: cliente.tipo_documento || 'CC',
        cliente_documento: cliente.numero_documento,
        // Traer fecha de evento si existe en ficha o cotización anterior
        fecha_evento: ultimaFicha?.fecha_evento || ultimaCot?.fecha_evento || prev.fecha_evento,
        // Traer vendedor si existe en última cotización
        ...(ultimaCot?.vendedor_id && {
          vendedor_id: ultimaCot.vendedor_id,
          vendedor_codigo: ultimaCot.vendedor_codigo,
          vendedor_nombre: ultimaCot.vendedor_nombre
        })
      }));
      
      // Cargar puntos JPM del cliente usando UUID
      try {
        const token = localStorage.getItem('token');
        const puntosResponse = await axios.get(`${API_URL}/api/puntos-jpm/cliente/${clienteIdUUID}/saldo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (puntosResponse.data.puntos_disponibles > 0) {
          setClientePuntos(puntosResponse.data);
        } else {
          setClientePuntos(null);
        }
      } catch (err) {
        console.log('No se pudieron cargar puntos:', err.message);
        setClientePuntos(null);
      }
      
      toast.success(`Cliente ${clienteIdDisplay || cliente.nombre} asociado con su historial`);
      setShowClienteModal(false);
    }
  };

  // Continuar sin asociar (crear cliente nuevo después)
  const continuarSinAsociar = () => {
    setShowClienteModal(false);
    toast.info('Puede crear un nuevo cliente después si lo desea');
  };
  
  // Desvincular cliente
  const desvincularCliente = () => {
    setQuote(prev => ({
      ...prev,
      cliente_id: '',
      puntos_jpm_a_redimir: 0,
      puntos_jpm_descuento: 0
    }));
    setClienteEncontrado(null);
    setClientePuntos(null);
    setPuntosARedimir(0);
    toast.info('Cliente desvinculado');
  };

  // Aplicar puntos JPM como descuento
  const aplicarPuntosJPM = () => {
    if (!clientePuntos || puntosARedimir <= 0) return;
    
    const puntosValidos = Math.min(puntosARedimir, clientePuntos.puntos_disponibles);
    const valorDescuento = puntosValidos * 100; // 1 punto = $100 COP
    
    setQuote(prev => ({
      ...prev,
      puntos_jpm_a_redimir: puntosValidos,
      puntos_jpm_descuento: valorDescuento
    }));
    
    setShowPuntosModal(false);
    toast.success(`Se aplicarán ${puntosValidos} Puntos JPM ($${valorDescuento.toLocaleString('es-CO')} de beneficio)`);
  };

  // Quitar puntos aplicados
  const quitarPuntosJPM = () => {
    setQuote(prev => ({
      ...prev,
      puntos_jpm_a_redimir: 0,
      puntos_jpm_descuento: 0
    }));
    setPuntosARedimir(0);
    toast.info('Puntos JPM removidos de la cotización');
  };

  // Obtener colecciones filtradas por marca
  const getColeccionesPorMarca = useCallback((marca) => {
    return catalog.colecciones_por_marca[marca] || [];
  }, [catalog.colecciones_por_marca]);

  // Obtener referencias filtradas - COLECCIÓN AHORA ES OPCIONAL
  const getReferencias = useCallback((marca, coleccion = '') => {
    // Si no hay colección seleccionada, mostrar todas las referencias de la marca
    const items = coleccion 
      ? catalog.catalog.filter(item => item.marca === marca && item.coleccion === coleccion)
      : catalog.catalog.filter(item => item.marca === marca);
    
    // Obtener referencias únicas
    const uniqueRefs = [...new Set(items.map(item => item.referencia))];
    return uniqueRefs;
  }, [catalog.catalog]);

  // Obtener precio de una combinación - COLECCIÓN AHORA ES OPCIONAL
  const getPrice = useCallback((marca, coleccion, referencia, tipo_prenda) => {
    let item;
    if (coleccion) {
      // Si hay colección, buscar exactamente
      item = catalog.catalog.find(
        i => i.marca === marca && i.coleccion === coleccion && i.referencia === referencia
      );
    } else {
      // Si no hay colección, buscar por marca y referencia (tomar el primero encontrado)
      item = catalog.catalog.find(
        i => i.marca === marca && i.referencia === referencia
      );
    }
    return item ? (item[tipo_prenda] || 0) : 0;
  }, [catalog.catalog]);
  
  // Obtener la colección automáticamente cuando se selecciona una referencia sin colección
  const getColeccionForReferencia = useCallback((marca, referencia) => {
    const item = catalog.catalog.find(
      i => i.marca === marca && i.referencia === referencia
    );
    return item ? item.coleccion : '';
  }, [catalog.catalog]);

  // ==================== FUNCIONES HELPER PARA CASCADA DEL COTIZADOR ====================
  // NUEVO ORDEN UX: TIPO → CONFECCIÓN → TALLA → EXPRESS → CANTIDAD → DTO% → PAÑO/REFERENCIA → PRECIO
  // Permite comparar diferentes telas sin reiniciar el flujo completo
  // OPTIMIZADO: Usa índices del backend y mapa de precios compacto (catalog.precios)
  // NOTA: El mapa de precios usa ||| como separador para evitar conflictos con | en referencias
  
  // Obtener tipos disponibles (desde índice del backend - ya filtrados por precio > 0)
  const getTiposConPrecio = useCallback(() => {
    return catalog.tipos || [];
  }, [catalog.tipos]);
  
  // Obtener confecciones disponibles para un tipo (desde índice del backend)
  const getConfeccionesPorTipo = useCallback((tipo) => {
    if (!catalog.confecciones_por_tipo || !tipo) return [];
    return catalog.confecciones_por_tipo[tipo] || [];
  }, [catalog.confecciones_por_tipo]);
  
  // Obtener referencias disponibles para tipo+confección (desde índice del backend)
  const getReferenciasPorTipoConfeccion = useCallback((tipo, confeccion) => {
    if (!catalog.referencias_por_tipo_confeccion) return [];
    const clave = `${tipo}|${confeccion}`;
    return catalog.referencias_por_tipo_confeccion[clave] || [];
  }, [catalog.referencias_por_tipo_confeccion]);
  
  // Obtener tallas disponibles para tipo+confección (unión de todas las referencias con precio)
  const getTallasParaTipoConfeccion = useCallback((tipo, confeccion) => {
    if (!catalog.precios || !tipo || !confeccion) return [];
    const tallasSet = new Set();
    const prefijo = `${tipo}|||${confeccion}|||`;
    // Buscar en el mapa de precios todas las claves que coincidan
    Object.keys(catalog.precios).forEach(clave => {
      if (clave.startsWith(prefijo)) {
        // clave = tipo|||confeccion|||referencia|||talla|||express
        const partes = clave.split('|||');
        if (partes.length === 5) {
          tallasSet.add(partes[3]); // talla está en índice 3
        }
      }
    });
    // Ordenar tallas en orden lógico
    const ordenTallas = {"S": 1, "M": 2, "L": 3, "XL": 4, "ÚNICA": 5, "A LA MEDIDA": 6, "INVITADO": 7};
    return Array.from(tallasSet).sort((a, b) => (ordenTallas[a] || 99) - (ordenTallas[b] || 99));
  }, [catalog.precios]);
  
  // Obtener opciones de express para tipo+confección+talla
  const getExpressParaTipoConfeccionTalla = useCallback((tipo, confeccion, talla) => {
    if (!catalog.precios || !tipo || !confeccion || !talla) return [];
    const expressSet = new Set();
    // Buscar en el mapa de precios
    Object.keys(catalog.precios).forEach(clave => {
      const partes = clave.split('|||');
      // clave = tipo|||confeccion|||referencia|||talla|||express
      if (partes.length === 5 && partes[0] === tipo && partes[1] === confeccion && partes[3] === talla) {
        expressSet.add(partes[4]); // express está en índice 4
      }
    });
    // Ordenar NO primero, SÍ después
    return Array.from(expressSet).sort((a, b) => (a === 'NO' ? -1 : 1));
  }, [catalog.precios]);
  
  // Obtener referencias filtradas por tipo+confección+talla+express (última selección del usuario)
  const getReferenciasFiltradas = useCallback((tipo, confeccion, talla, express) => {
    if (!catalog.precios || !tipo || !confeccion || !talla || !express) return [];
    const referenciasSet = new Set();
    // Buscar en el mapa de precios
    Object.keys(catalog.precios).forEach(clave => {
      const partes = clave.split('|||');
      // clave = tipo|||confeccion|||referencia|||talla|||express
      if (partes.length === 5 && partes[0] === tipo && partes[1] === confeccion && partes[3] === talla && partes[4] === express) {
        referenciasSet.add(partes[2]); // referencia está en índice 2
      }
    });
    return Array.from(referenciasSet).sort();
  }, [catalog.precios]);
  
  // Helper para normalizar etiquetas de tela (eliminar saltos de línea y espacios múltiples)
  const normalizarLabelTela = useCallback((ref) => {
    if (!ref) return '';
    return ref
      .replace(/[\r\n]+/g, ' ')  // Reemplazar saltos de línea con espacio
      .replace(/\s+/g, ' ')       // Colapsar espacios múltiples
      .trim();                    // Eliminar espacios al inicio/final
  }, []);
  
  // Obtener tallas disponibles para tipo+confección+referencia (cascada original - mantener para compatibilidad)
  const getTallasPorCombinacion = useCallback((tipo, confeccion, referencia) => {
    if (!catalog.tallas_por_combinacion) return [];
    const clave = `${tipo}|${confeccion}|${referencia}`;
    return catalog.tallas_por_combinacion[clave] || [];
  }, [catalog.tallas_por_combinacion]);
  
  // Obtener opciones de express para tipo+confección+referencia+talla (cascada original - mantener para compatibilidad)
  const getExpressPorCombinacion = useCallback((tipo, confeccion, referencia, talla) => {
    if (!catalog.express_por_combinacion) return [];
    const clave = `${tipo}|${confeccion}|${referencia}|${talla}`;
    return catalog.express_por_combinacion[clave] || [];
  }, [catalog.express_por_combinacion]);
  
  // Obtener precio del Cotizador para una combinación completa (OPTIMIZADO: usa mapa de precios con |||)
  const getPrecioCotizador = useCallback((tipo, confeccion, referencia, talla, express) => {
    if (!catalog.precios) return 0;
    const clave = `${tipo}|||${confeccion}|||${referencia}|||${talla}|||${express}`;
    return catalog.precios[clave] || 0;
  }, [catalog.precios]);
  
  // Verificar si la combinación es válida y tiene precio
  const esCombinacionValida = useCallback(() => {
    const { tipo, confeccion, pano_referencia, talla, express } = newItemCotizador;
    if (!tipo || !confeccion || !pano_referencia || !talla || !express) return false;
    const precio = getPrecioCotizador(tipo, confeccion, pano_referencia, talla, express);
    return precio > 0;
  }, [newItemCotizador, getPrecioCotizador]);

  // ==================== FIN FUNCIONES HELPER COTIZADOR ====================

  // Calcular totales con descuento porcentual por ítem y descuento general
  const calcularTotalItem = (item) => {
    const valorBase = item.precio_base * item.cantidad;
    const descuentoItem = Math.max(0, Math.min(100, item.descuento_porcentaje || 0));
    const valorDescuento = Math.round(valorBase * descuentoItem / 100);
    return valorBase - valorDescuento;
  };
  
  const subtotal = quote.items.reduce((sum, item) => sum + calcularTotalItem(item), 0);
  const descuentoPorcentaje = Math.max(0, Math.min(100, quote.descuento_porcentaje || 0));
  const descuentoValor = Math.round(subtotal * descuentoPorcentaje / 100);
  const subtotalConDescuento = subtotal - descuentoValor;
  
  // Aplicar descuento de Puntos JPM
  const descuentoPuntosJPM = quote.puntos_jpm_descuento || 0;
  const total = subtotalConDescuento - descuentoPuntosJPM;

  // Calcular valores del plan de pagos
  const porcentajeAnticipo = quote.porcentaje_anticipo || 70;
  const numeroPagos = quote.numero_pagos || 3;
  const anticipoValor = Math.round(total * porcentajeAnticipo / 100);
  const saldoValor = total - anticipoValor;
  const valorPorPago = Math.round(anticipoValor / numeroPagos);

  // Helper para formatear precio con la moneda de la cotización
  const fp = useCallback((value) => formatPrice(value, quote.moneda), [quote.moneda]);
  
  // Helper para convertir precio de COP a la moneda de la cotización
  // Los precios del Cotizador siempre vienen en COP, se convierten al mostrar/guardar
  const convertirPrecioCotizador = useCallback((precioCOP) => {
    if (!precioCOP || precioCOP <= 0) return 0;
    return convertirDeCOP(precioCOP, quote.moneda);
  }, [quote.moneda]);

  // Formatear fecha para mostrar
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'Por definir';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Calcular fecha límite (60 días antes del evento)
  const calcularFechaLimite = useCallback((fechaEventoStr) => {
    if (!fechaEventoStr) return null;
    const fechaEvento = new Date(fechaEventoStr);
    const fechaLimite = new Date(fechaEvento);
    fechaLimite.setDate(fechaLimite.getDate() - DIAS_LIMITE);
    return fechaLimite;
  }, []);

  // Generar plan de pagos con las reglas correctas
  const generatePlanPagos = useCallback(() => {
    // Validación: fecha del evento
    if (!quote.fecha_evento) {
      toast.error('Ingrese la fecha del evento para generar el plan de pagos');
      return;
    }

    // Validación: items en cotización
    if (total <= 0) {
      toast.error('Agregue items a la cotización antes de generar el plan');
      return;
    }

    // Validación: máximo 6 cuotas para el 70%
    if (numeroPagos > MAX_CUOTAS_70) {
      toast.error(`El 70% puede dividirse en máximo ${MAX_CUOTAS_70} cuotas`);
      return;
    }

    const fechaEvento = new Date(quote.fecha_evento);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    // Calcular fecha límite (60 días antes del evento)
    const fechaLimite = new Date(fechaEvento);
    fechaLimite.setDate(fechaLimite.getDate() - DIAS_LIMITE);

    // Calcular valores considerando el anticipo previo
    const anticipoPrevio = Number(quote.anticipo_previo) || 0;
    const totalPendiente = total - anticipoPrevio;
    
    if (totalPendiente <= 0) {
      toast.error('El anticipo previo supera o iguala el total. No es necesario un plan de pagos.');
      return;
    }
    
    const valor70Total = Math.round(total * PORCENTAJE_ANTICIPO / 100);
    const valor70Pendiente = Math.max(0, valor70Total - anticipoPrevio); // Lo que falta del 70%
    const valor30 = total - valor70Total; // Saldo contra entrega (30%)

    // Si el anticipo ya cubrió el 70%, solo falta el 30%
    if (valor70Pendiente <= 0) {
      toast.info('El anticipo previo ya cubrió el 70%. Solo queda el saldo contra entrega.');
      const nuevosPagos = [{
        numero: 1,
        fecha: quote.fecha_evento,
        concepto: `Saldo contra entrega (30%)`,
        valor: valor30,
        acumulado: anticipoPrevio + valor30
      }];
      setQuote(prev => ({ ...prev, plan_pagos: nuevosPagos }));
      return;
    }

    // Calcular valor por cuota para el 70% pendiente
    let valorPorCuota = Math.round(valor70Pendiente / numeroPagos);

    // Obtener la moneda de la cotización y calcular el mínimo equivalente
    const monedaCotizacion = quote.moneda || 'COP';
    
    // Calcular el mínimo de cuota en la moneda de la cotización según el rol
    // Admin/Gerencia: $500.000 COP | Otros: $1.000.000 COP
    const minimoEnMoneda = getMinimoEnMoneda(monedaCotizacion, userRole);
    const esPrivilegiado = puedeUsarRangoFlexible(userRole);
    const minimoCOPTexto = esPrivilegiado ? '$500.000' : '$1.000.000';

    // MODO DE PLAN DE PAGOS (solo para admin/gerencia)
    // 'manual' = Plan libre sin restricciones | 'automatico' o vacío = reglas normales
    const usarPlanManual = esPrivilegiado && quote.modo_plan_pagos === 'manual';
    
    if (usarPlanManual) {
      // Admin/Gerencia: Plan de pagos LIBRE sin restricciones
      const valorPrimeraCuota = Number(quote.valor_primera_cuota) || 0;
      
      if (valorPrimeraCuota > 0) {
        // Si ingresó un valor para la primera cuota, usarlo y distribuir el resto
        const restante = valor70Pendiente - valorPrimeraCuota;
        const cuotasRestantes = numeroPagos - 1;
        
        if (cuotasRestantes > 0 && restante > 0) {
          valorPorCuota = Math.round(restante / cuotasRestantes);
        } else if (cuotasRestantes === 0 || restante <= 0) {
          // Solo 1 cuota o la primera cuota cubre todo
          valorPorCuota = valorPrimeraCuota;
        }
        
        toast.success(`Plan libre: Primera cuota ${fp(valorPrimeraCuota)}`);
      } else {
        // No ingresó valor, distribuir equitativamente (sin validación de mínimo)
        valorPorCuota = Math.round(valor70Pendiente / numeroPagos);
        toast.success(`Plan libre: ${numeroPagos} cuotas de ${fp(valorPorCuota)}`);
      }
    } else {
      // VALIDACIÓN NORMAL: Comparar en la misma moneda (la de la cotización)
      // valorPorCuota (en moneda de cotización) vs minimoEnMoneda (mínimo COP convertido a moneda de cotización)
      
      if (valorPorCuota < minimoEnMoneda && numeroPagos > 1) {
        // La cuota no alcanza el mínimo - intentar ajustar
        // Primera cuota = mínimo, distribuir el resto entre las demás cuotas
        const primerPago = minimoEnMoneda;
        const restante = valor70Pendiente - primerPago;
        const cuotasRestantes = numeroPagos - 1;
        
        if (restante <= 0) {
          // El mínimo excede el 70% completo
          const minimoFormateado = fp(minimoEnMoneda);
          const valor70Formateado = fp(valor70Pendiente);
          
          // Sugerir usar primera cuota manual si es privilegiado
          if (esPrivilegiado) {
            toast.error(
              `El primer pago mínimo (${minimoFormateado}) excede el 70% pendiente (${valor70Formateado}). Active "Primera cuota manual" para ingresar un valor personalizado.`
            );
          } else {
            toast.error(
              `El primer pago mínimo (${minimoFormateado}, equiv. a ${minimoCOPTexto} COP) excede el 70% pendiente (${valor70Formateado}). Reduzca el número de cuotas.`
            );
          }
          return;
        }
        
        // Ajustar: primera cuota al mínimo, resto distribuido
        valorPorCuota = Math.round(restante / cuotasRestantes);
      } else if (valorPorCuota < minimoEnMoneda && numeroPagos === 1) {
        // Solo 1 cuota pero no alcanza el mínimo
        const minimoFormateado = fp(minimoEnMoneda);
        const cuotaFormateada = fp(valorPorCuota);
        
        if (esPrivilegiado) {
          toast.error(
            `La cuota (${cuotaFormateada}) es menor al mínimo (${minimoFormateado}). Active "Primera cuota manual" para usar un valor personalizado.`
          );
        } else {
          toast.error(
            `Cada cuota debe equivaler al menos a ${minimoCOPTexto} COP. En ${MONEDAS[monedaCotizacion]?.nombre || monedaCotizacion}, el mínimo es ${minimoFormateado}. El valor calculado es ${cuotaFormateada}.`
          );
        }
        return;
      }
    }

    // Verificar que hay suficiente tiempo antes del evento
    const diasHastaLimite = Math.floor((fechaLimite - hoy) / (1000 * 60 * 60 * 24));
    
    if (diasHastaLimite < 0) {
      toast.error('La fecha límite (60 días antes del evento) ya pasó. El 70% pendiente debe pagarse de inmediato.');
      // Generar plan con pago único del 70% pendiente
      const nuevosPagos = [
        {
          numero: 1,
          fecha: hoy.toISOString().split('T')[0],
          concepto: anticipoPrevio > 0 ? `Saldo del 70% (fecha límite vencida)` : `Pago del 70% (fecha límite vencida)`,
          valor: valor70Pendiente,
          acumulado: anticipoPrevio + valor70Pendiente
        },
        {
          numero: 2,
          fecha: quote.fecha_evento,
          concepto: `Pago final contra entrega (30%)`,
          valor: valor30,
          acumulado: total
        }
      ];
      setQuote(prev => ({ ...prev, plan_pagos: nuevosPagos }));
      return;
    }

    // Calcular intervalo entre pagos (mínimo 7 días)
    const intervalo = Math.max(7, Math.floor(diasHastaLimite / numeroPagos));

    const nuevosPagos = [];
    let acumulado = anticipoPrevio; // Empezar desde el anticipo previo

    // Si hay anticipo previo, agregar nota informativa
    if (anticipoPrevio > 0) {
      nuevosPagos.push({
        numero: 0,
        fecha: '-',
        concepto: `✓ Anticipo previo registrado`,
        valor: anticipoPrevio,
        acumulado: anticipoPrevio,
        esPrevio: true // Marcador para styling especial
      });
    }

    // Generar las cuotas del 70% pendiente
    for (let i = 0; i < numeroPagos; i++) {
      const fechaPago = new Date(hoy);
      fechaPago.setDate(fechaPago.getDate() + (intervalo * (i + 1)));
      
      // Asegurar que no exceda la fecha límite
      if (fechaPago > fechaLimite) {
        fechaPago.setTime(fechaLimite.getTime());
      }

      // Calcular valor de la cuota
      let valor;
      if (i === 0) {
        // Primera cuota: usar valor manual si está activo, sino el máximo entre mínimo y valor calculado
        if (usarPlanManual) {
          const valorManual = Number(quote.valor_primera_cuota) || 0;
          valor = valorManual > 0 ? valorManual : valorPorCuota;
        } else {
          valor = Math.max(minimoEnMoneda, valorPorCuota);
        }
      } else if (i === numeroPagos - 1) {
        // Última cuota del 70%: ajustar para completar exactamente
        const yaAcumulado = acumulado - anticipoPrevio;
        valor = Math.max(0, valor70Pendiente - yaAcumulado);
      } else {
        valor = valorPorCuota;
      }
      
      // Asegurar que el valor sea entero y no negativo
      valor = Math.max(0, Math.round(valor));
      
      // Si el valor es 0 y no es la última cuota, saltar (evitar cuotas vacías)
      if (valor === 0 && i < numeroPagos - 1) {
        continue;
      }
      
      acumulado += valor;

      // Conceptos descriptivos
      let concepto;
      if (i === 0 && anticipoPrevio > 0) {
        concepto = `Cuota ${i + 1} - Continúa el 70%`;
      } else if (i === 0) {
        concepto = `Primer pago (${Math.round(valor / total * 100)}% del total)`;
      } else if (i === numeroPagos - 1) {
        concepto = `Cuota ${i + 1} - Completa el 70%`;
      } else {
        concepto = `Cuota ${i + 1} del plan de pagos`;
      }

      nuevosPagos.push({
        numero: anticipoPrevio > 0 ? i + 1 : i + 1,
        fecha: fechaPago.toISOString().split('T')[0],
        concepto: concepto,
        valor: valor,
        acumulado: acumulado
      });
    }

    // Agregar el PAGO FINAL CONTRA ENTREGA (30%)
    nuevosPagos.push({
      numero: numeroPagos + 1,
      fecha: quote.fecha_evento, // En la fecha del evento
      concepto: `Pago final contra entrega (30%)`,
      valor: valor30,
      acumulado: total
    });

    // Verificar y ajustar redondeo si es necesario
    const totalCalculado = nuevosPagos.filter(p => !p.esPrevio).reduce((sum, p) => sum + p.valor, 0) + anticipoPrevio;
    if (totalCalculado !== total) {
      const diferencia = total - totalCalculado;
      // Ajustar en el saldo final
      const lastIndex = nuevosPagos.length - 1;
      nuevosPagos[lastIndex].valor += diferencia;
    }

    setQuote(prev => ({ ...prev, plan_pagos: nuevosPagos }));
    const msgAnticipo = anticipoPrevio > 0 ? ` (anticipo previo: ${fp(anticipoPrevio)})` : '';
    toast.success(`Plan generado: ${numeroPagos} cuotas para el 70% + pago final${msgAnticipo}`);
  }, [quote.fecha_evento, quote.moneda, quote.anticipo_previo, total, numeroPagos, formatPrice]);

  // Validar plan de pagos antes de guardar
  const validarPlanPagos = useCallback(() => {
    if (quote.metodo_pago !== 'plan_pagos' || !quote.plan_pagos?.length) {
      return { valid: true, errors: [] };
    }

    const errores = [];
    const fechaLimite = calcularFechaLimite(quote.fecha_evento);
    const valor70 = Math.round(total * PORCENTAJE_ANTICIPO / 100);

    // Obtener moneda y mínimo para la validación según el rol
    const monedaCotizacion = quote.moneda || 'COP';
    const minimoEnMoneda = getMinimoEnMoneda(monedaCotizacion, userRole);
    const esPrivilegiado = puedeUsarRangoFlexible(userRole);
    const minimoCOPTexto = esPrivilegiado ? '$500.000' : '$1.000.000';
    const primerPagoValor = quote.plan_pagos[0]?.valor || 0;
    
    // Si está activo el modo manual, no validar el mínimo
    const usarPlanManual = esPrivilegiado && quote.modo_plan_pagos === 'manual';

    // VALIDACIÓN: Solo aplicar mínimo si NO está usando plan manual
    if (!usarPlanManual && primerPagoValor < minimoEnMoneda) {
      const minimoFormateado = formatPrice(minimoEnMoneda, monedaCotizacion);
      const pagoFormateado = formatPrice(primerPagoValor, monedaCotizacion);
      errores.push(`El primer pago (${pagoFormateado}) es menor al mínimo requerido (${minimoFormateado}, equivalente a ${minimoCOPTexto} COP).`);
    }

    // Validar máximo 6 cuotas para el 70% (excluyendo el pago final)
    const cuotas70 = quote.plan_pagos.slice(0, -1);
    if (cuotas70.length > MAX_CUOTAS_70) {
      errores.push(`El 70% puede dividirse en máximo ${MAX_CUOTAS_70} cuotas`);
    }

    // Validar fechas no vacías
    const fechasVacias = quote.plan_pagos.filter(p => !p.fecha);
    if (fechasVacias.length > 0) {
      errores.push('No se permiten fechas vacías en el plan de pagos');
    }

    // Validar cuotas en cero
    const cuotasCero = quote.plan_pagos.filter(p => p.valor <= 0);
    if (cuotasCero.length > 0) {
      errores.push('No se permiten cuotas con valor en cero');
    }

    // Validar que el 70% esté pagado antes de la fecha límite
    if (fechaLimite) {
      let acumulado70 = 0;
      for (let i = 0; i < quote.plan_pagos.length - 1; i++) { // Excluir pago final
        const pago = quote.plan_pagos[i];
        if (pago.fecha) {
          const fechaPago = new Date(pago.fecha);
          if (fechaPago <= fechaLimite) {
            acumulado70 += pago.valor;
          } else {
            errores.push(`La cuota ${pago.numero} (${fp(pago.valor)}) excede la fecha límite`);
          }
        }
      }
      if (acumulado70 < valor70 - 1) { // Tolerancia de $1
        errores.push(`El 70% (${fp(valor70)}) debe completarse antes de 60 días del evento. Actual: ${fp(acumulado70)}`);
      }
    }

    // Validar total
    const totalPlan = quote.plan_pagos.reduce((sum, p) => sum + p.valor, 0);
    if (Math.abs(totalPlan - total) > 1) {
      errores.push(`El total del plan (${fp(totalPlan)}) no coincide con el total (${fp(total)})`);
    }

    return { valid: errores.length === 0, errors: errores };
  }, [quote.metodo_pago, quote.plan_pagos, quote.fecha_evento, total, calcularFechaLimite, formatPrice]);

  // Verificar si alguna fecha del plan excede el límite
  const verificarFechasLimite = useCallback(() => {
    if (!quote.fecha_evento || !quote.plan_pagos?.length) return null;
    
    const fechaLimite = calcularFechaLimite(quote.fecha_evento);
    if (!fechaLimite) return null;

    // Encontrar pagos del 70% que excedan la fecha límite (excluir pago final)
    const pagosConProblema = quote.plan_pagos.filter((pago, idx) => {
      if (!pago.fecha || idx === quote.plan_pagos.length - 1) return false;
      const fechaPago = new Date(pago.fecha);
      return fechaPago > fechaLimite;
    });

    return pagosConProblema.length > 0 ? pagosConProblema : null;
  }, [quote.fecha_evento, quote.plan_pagos, calcularFechaLimite]);

  const fechasConProblema = verificarFechasLimite();

  // ==================== AGREGAR ITEM DESDE COTIZADOR ====================
  const handleAddItemCotizador = () => {
    const { tipo, confeccion, pano_referencia, talla, express, cantidad, descripcion_manual, notas_internas, descuento_porcentaje, precio_base } = newItemCotizador;
    
    // Validaciones
    if (!tipo || !confeccion || !pano_referencia || !talla || !express) {
      toast.error('Complete todos los campos: Tipo, Confección, Paño/Referencia, Talla y Express');
      return;
    }
    
    if (!descripcion_manual || descripcion_manual.trim().length < 10) {
      toast.error('Escriba una descripción comercial para el cliente (mínimo 10 caracteres)');
      return;
    }
    
    if (!precio_base || precio_base <= 0) {
      toast.error('No se encontró precio para esta combinación. Verifique que la tarifa exista en el Cotizador.');
      return;
    }
    
    // Convertir precio de COP a la moneda de la cotización
    const precioConvertido = Math.round(convertirPrecioCotizador(precio_base));
    
    // Crear item
    const item = {
      id: Date.now().toString(),
      tipo_item: 'cotizador',
      // Datos del Cotizador
      tipo_cotizador: tipo,
      confeccion,  // Código interno (Traje A, Traje B, etc.)
      confeccion_label: getConfeccionLabel(confeccion),  // Nombre descriptivo para mostrar
      pano_referencia,  // Valor exacto de la matriz
      talla,
      express,
      // Descripción que aparece en el PDF
      descripcion_manual: descripcion_manual.trim(),
      // Precio convertido a la moneda de la cotización (redondeado a entero)
      precio_base: precioConvertido,
      cantidad,
      precio_cliente: precioConvertido * cantidad,
      // Descuento del artículo (0-100%)
      descuento_porcentaje: Math.max(0, Math.min(100, descuento_porcentaje || 0)),
      // Notas internas (NO aparecen en PDF del cliente)
      notas_internas: notas_internas?.trim() || '',
      // Referencia interna para auditoría (precio original en COP)
      referencia_interna: {
        tipo: 'cotizador',
        tipo_cotizador: tipo,
        confeccion,
        confeccion_label: getConfeccionLabel(confeccion),
        pano_referencia,
        talla,
        express,
        precio_cotizador_cop: precio_base,  // Precio original en COP para auditoría
        moneda_cotizacion: quote.moneda,
        tasa_conversion: MONEDAS[quote.moneda]?.tasaCOP || 1
      }
    };
    
    setQuote(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));
    
    // Limpiar formulario
    setNewItemCotizador({
      tipo: '',
      confeccion: '',
      pano_referencia: '',
      talla: '',
      express: '',
      cantidad: 1,
      descripcion_manual: '',
      notas_internas: '',
      descuento_porcentaje: 0,
      precio_base: 0
    });
    
    toast.success('Producto del Cotizador agregado');
  };

  // Agregar item desde Lista de Precios (legacy)
  const handleAddItemListaPrecios = () => {
    if (!newItemListaPrecios.marca || !newItemListaPrecios.referencia || !newItemListaPrecios.tipo_prenda) {
      toast.error('Complete marca, referencia y tipo de prenda');
      return;
    }
    
    if (!newItemListaPrecios.descripcion_manual || newItemListaPrecios.descripcion_manual.trim().length < 10) {
      toast.error('Escriba una descripción comercial para el cliente (mínimo 10 caracteres)');
      return;
    }

    // Si no hay colección seleccionada, obtenerla automáticamente
    const coleccion = newItemListaPrecios.coleccion || getColeccionForReferencia(newItemListaPrecios.marca, newItemListaPrecios.referencia);
    const precio_base = Math.round(getPrice(newItemListaPrecios.marca, coleccion, newItemListaPrecios.referencia, newItemListaPrecios.tipo_prenda) || 0);
    
    // Generar referencia interna automática
    const referenciaInterna = {
      tipo: 'lista_precios',
      marca: newItemListaPrecios.marca,
      coleccion: coleccion,
      referencia_tela: newItemListaPrecios.referencia,
      tipo_prenda: newItemListaPrecios.tipo_prenda,
      precio_asociado: precio_base,
      cantidad: newItemListaPrecios.cantidad
    };
    
    const item = {
      id: Date.now().toString(),
      tipo_item: 'lista_precios',
      // Datos internos (no se muestran al cliente)
      fuente: newItemListaPrecios.marca,
      marca: newItemListaPrecios.marca,
      coleccion: coleccion,
      referencia: newItemListaPrecios.referencia,
      tipo_prenda: newItemListaPrecios.tipo_prenda,
      // Descripción que aparece en el PDF
      descripcion_manual: newItemListaPrecios.descripcion_manual.trim(),
      precio_base,
      cantidad: newItemListaPrecios.cantidad,
      precio_cliente: precio_base * newItemListaPrecios.cantidad,
      // Descuento del artículo (0-100%)
      descuento_porcentaje: Math.max(0, Math.min(100, newItemListaPrecios.descuento_porcentaje || 0)),
      // Información interna (NO aparece en PDF del cliente)
      referencia_interna: referenciaInterna,
      notas_internas: newItemListaPrecios.notas_internas?.trim() || ''
    };

    setQuote(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItemListaPrecios({
      marca: '',
      coleccion: '',
      pano_referencia: '',
      tipo_prenda: '',
      cantidad: 1,
      descripcion_manual: '',
      notas_internas: '',
      descuento_porcentaje: 0
    });

    toast.success('Producto agregado');
  };

  // Agregar item Manual (Accesorio)
  const handleAddItemManual = () => {
    if (!newItemManual.nombre || newItemManual.precio_base <= 0) {
      toast.error('Ingrese el nombre y precio del producto');
      return;
    }
    
    if (!newItemManual.descripcion_manual || newItemManual.descripcion_manual.trim().length < 5) {
      toast.error('Escriba una descripción para el cliente');
      return;
    }

    const item = {
      id: Date.now().toString(),
      tipo_item: 'manual',
      nombre: newItemManual.nombre,
      descripcion_manual: newItemManual.descripcion_manual.trim(),
      precio_base: Math.round(newItemManual.precio_base || 0),
      cantidad: newItemManual.cantidad,
      precio_cliente: Math.round((newItemManual.precio_base || 0) * newItemManual.cantidad),
      // Descuento del artículo (0-100%)
      descuento_porcentaje: Math.max(0, Math.min(100, newItemManual.descuento_porcentaje || 0)),
      // Información interna (NO aparece en PDF del cliente)
      referencia_material: newItemManual.referencia_material?.trim() || '',
      notas_internas: newItemManual.notas_internas?.trim() || ''
    };

    setQuote(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItemManual({
      nombre: '',
      descripcion_manual: '',
      precio_base: 0,
      cantidad: 1,
      referencia_material: '',
      notas_internas: '',
      descuento_porcentaje: 0
    });

    toast.success('Accesorio agregado');
  };

  // Agregar Servicio
  const handleAddItemServicio = () => {
    if (!newItemServicio.nombre || newItemServicio.precio_base <= 0) {
      toast.error('Ingrese el nombre y precio del servicio');
      return;
    }
    
    if (!newItemServicio.descripcion_manual || newItemServicio.descripcion_manual.trim().length < 5) {
      toast.error('Escriba una descripción para el cliente');
      return;
    }

    const item = {
      id: Date.now().toString(),
      tipo_item: 'servicio',
      nombre: newItemServicio.nombre,
      descripcion_manual: newItemServicio.descripcion_manual.trim(),
      precio_base: Math.round(newItemServicio.precio_base || 0),
      cantidad: newItemServicio.cantidad,
      precio_cliente: Math.round((newItemServicio.precio_base || 0) * newItemServicio.cantidad),
      // Descuento del artículo (0-100%)
      descuento_porcentaje: Math.max(0, Math.min(100, newItemServicio.descuento_porcentaje || 0)),
      // Información interna (NO aparece en PDF del cliente)
      notas_internas: newItemServicio.notas_internas?.trim() || ''
    };

    setQuote(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItemServicio({
      nombre: '',
      descripcion_manual: '',
      precio_base: 0,
      cantidad: 1,
      notas_internas: '',
      descuento_porcentaje: 0
    });

    toast.success('Servicio agregado');
  };

  // Eliminar item
  const handleRemoveItem = (itemId) => {
    setQuote(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  // Actualizar cantidad de item
  const handleUpdateQuantity = (itemId, cantidad) => {
    if (cantidad < 1) return;
    setQuote(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId 
          ? { ...item, cantidad, precio_cliente: item.precio_base * cantidad }
          : item
      )
    }));
  };

  // Actualizar descuento de item (0-100%)
  const handleUpdateItemDiscount = (itemId, descuento) => {
    const descuentoValido = Math.max(0, Math.min(100, parseInt(descuento) || 0));
    setQuote(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId 
          ? { ...item, descuento_porcentaje: descuentoValido }
          : item
      )
    }));
  };

  // Guardar cotización
  const handleSave = async (newEstado = null) => {
    if (!quote.cliente_nombre) {
      toast.error('El nombre del cliente es requerido');
      return;
    }
    
    if (!quote.fecha_evento) {
      toast.error('La fecha del evento es obligatoria');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Asegurar que todos los precios sean enteros (para evitar errores con MXN/USD)
      const itemsLimpios = quote.items.map(item => ({
        ...item,
        precio_base: Math.round(item.precio_base || 0),
        precio_cliente: Math.round((item.precio_base || 0) * (item.cantidad || 1)),
        cantidad: item.cantidad || 1
      }));
      
      const data = {
        ...quote,
        estado: newEstado || quote.estado,
        subtotal: Math.round(quote.subtotal || 0),
        total: Math.round(quote.total || 0),
        items: itemsLimpios,
        plan_pagos: quote.plan_pagos?.map(p => ({ ...p, valor: Math.round(p.valor || 0), acumulado: Math.round(p.acumulado || 0) }))
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/quotes/${id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cotización actualizada');
      } else {
        const response = await axios.post(`${API_URL}/api/quotes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Cotización ${response.data.numero} creada`);
        navigate(`/quotes/${response.data.id}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al guardar la cotización';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Duplicar cotización
  const handleDuplicate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/quotes/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cotización duplicada: ${response.data.numero}`);
      navigate(`/quotes/${response.data.id}`);
    } catch (error) {
      toast.error('Error al duplicar la cotización');
    }
  };

  // Descargar PDF
  // Descargar PDF
  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_${quote.numero || 'JPM'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar el PDF');
    }
  };

  // Estado para el proceso de WhatsApp
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Emojis generados con String.fromCodePoint para evitar problemas de codificación
  const EMOJI = {
    sparkle: String.fromCodePoint(0x2728),
    blackHeart: String.fromCodePoint(0x1F5A4),
    wave: String.fromCodePoint(0x1F44B, 0x1F3FB),
    needle: String.fromCodePoint(0x1FAA1),
    groom: String.fromCodePoint(0x1F935, 0x1F3FB, 0x200D, 0x2642, 0xFE0F),
    receipt: String.fromCodePoint(0x1F9FE),
    briefcase: String.fromCodePoint(0x1F4BC),
    link: String.fromCodePoint(0x1F4CE)
  };

  // Función para generar el mensaje de WhatsApp con emojis seguros
  const generarMensajeWhatsApp = (nombreCliente, numeroCotizacion, valorTotal, linkPdf) => {
    return `${EMOJI.sparkle} *JUAN PABLO MURILLO* ${EMOJI.sparkle}
_Alta Sastreria a la Medida_ ${EMOJI.blackHeart}

Hola *${nombreCliente}*, es un gusto saludarte. ${EMOJI.wave}

Gracias por confiar en *Juan Pablo Murillo* para la creacion de una prenda unica, disenada a la medida con elegancia, precision y atencion a cada detalle. ${EMOJI.needle}${EMOJI.groom}

Te compartimos tu cotizacion personalizada:

${EMOJI.receipt} *Cotizacion:* ${numeroCotizacion}
${EMOJI.briefcase} *Valor total:* ${valorTotal}
${EMOJI.link} *Ver cotizacion:* ${linkPdf}

Cada pieza es elaborada bajo un concepto de alta sastreria, con materiales seleccionados, acabados refinados y una experiencia pensada para acompanarte en un momento especial. ${EMOJI.sparkle}

Sera un placer asesorarte en la eleccion de telas, diseno, detalles y acabados para lograr una prenda impecable. ${EMOJI.blackHeart}

*Juan Pablo Murillo*
_Alta Sastreria a la Medida_`;
  };

  // Enviar cotización por WhatsApp - Flujo mejorado
  const handleSendWhatsApp = async () => {
    // Validar que la cotización esté guardada
    if (!isEditing) {
      toast.error('Guarda la cotización primero antes de enviarla');
      return;
    }

    // Validar que haya items
    if (!quote.items || quote.items.length === 0) {
      toast.error('Agrega items a la cotización antes de enviarla');
      return;
    }

    setSendingWhatsApp(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Paso 1: Generar enlace público para el PDF
      const linkResponse = await axios.post(
        `${API_URL}/api/quotes/${id}/generate-public-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const publicToken = linkResponse.data.token;
      const pdfUrl = `${API_URL}/api/quotes/public/${id}/pdf?token=${publicToken}`;

      // Paso 2: Descargar el PDF automáticamente
      toast.info('Descargando PDF de la cotización...');
      const pdfResponse = await axios.get(`${API_URL}/api/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crear enlace de descarga
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.setAttribute('download', `Cotizacion_${quote.numero || 'JPM'}.pdf`);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      // Preparar número de WhatsApp
      let phoneNumber = quote.cliente_telefono || '';
      phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
      if (phoneNumber.startsWith('+')) {
        phoneNumber = phoneNumber.substring(1);
      }
      if (phoneNumber.length === 10 && phoneNumber.startsWith('3')) {
        phoneNumber = '57' + phoneNumber;
      }

      // Limpiar nombre del cliente (quitar espacios extra)
      const nombreCliente = (quote.cliente_nombre || 'Cliente').trim();

      // Generar mensaje con emojis seguros (usando String.fromCodePoint)
      const mensaje = generarMensajeWhatsApp(
        nombreCliente,
        quote.numero || 'Nueva',
        fp(total),
        pdfUrl
      );

      // Validar que no haya caracteres dañados
      if (mensaje.includes('\uFFFD')) {
        console.error('ERROR: el mensaje contiene caracteres dañados:', mensaje);
        toast.error('Error de codificación en el mensaje. Contacta soporte.');
        return;
      }

      // Codificar mensaje para URL (una sola vez)
      const mensajeCodificado = encodeURIComponent(mensaje);

      // Crear URL de WhatsApp
      let whatsappUrl;
      if (phoneNumber) {
        whatsappUrl = `https://wa.me/${phoneNumber}?text=${mensajeCodificado}`;
      } else {
        whatsappUrl = `https://wa.me/?text=${mensajeCodificado}`;
        toast.info('No hay teléfono registrado. Selecciona el contacto en WhatsApp.');
      }

      // Pequeña pausa para que el usuario vea que se descargó el PDF
      await new Promise(resolve => setTimeout(resolve, 800));

      // Abrir WhatsApp
      window.open(whatsappUrl, '_blank');
      toast.success('PDF descargado. WhatsApp abierto. Adjunta el PDF manualmente en el chat.', { duration: 6000 });
      
    } catch (error) {
      console.error('Error en WhatsApp:', error);
      toast.error('Error al preparar el envío por WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Vista previa del PDF con validación robusta
  const handlePreviewPDF = async () => {
    // Validar datos antes de generar la vista previa
    const validationErrors = [];
    
    // Validar cliente
    if (!quote.cliente_nombre || quote.cliente_nombre.trim() === '') {
      validationErrors.push('Nombre del cliente');
    }
    
    // Validar items
    if (!quote.items || quote.items.length === 0) {
      validationErrors.push('Al menos un ítem');
    } else {
      // Validar cada item
      for (let i = 0; i < quote.items.length; i++) {
        const item = quote.items[i];
        const itemNum = i + 1;
        
        // Validar descripcion_manual
        if (!item.descripcion_manual || item.descripcion_manual.trim() === '') {
          validationErrors.push(`Descripción del ítem ${itemNum}`);
        }
        
        // Validar cantidad
        if (!item.cantidad || isNaN(item.cantidad) || item.cantidad < 1) {
          validationErrors.push(`Cantidad del ítem ${itemNum}`);
        }
        
        // Validar precio
        if (item.precio_base === null || item.precio_base === undefined || isNaN(item.precio_base) || item.precio_base < 0) {
          validationErrors.push(`Precio del ítem ${itemNum}`);
        }
      }
    }
    
    // Validar descuento
    const descPct = quote.descuento_porcentaje;
    if (descPct !== null && descPct !== undefined && (isNaN(descPct) || descPct < 0 || descPct > 100)) {
      validationErrors.push('Porcentaje de descuento inválido');
    }
    
    // Si hay errores, mostrar mensaje y no continuar
    if (validationErrors.length > 0) {
      toast.error(`Faltan datos para generar la vista previa: ${validationErrors.slice(0, 3).join(', ')}${validationErrors.length > 3 ? '...' : ''}`);
      return;
    }
    
    // Validar que la cotización esté guardada
    if (!id) {
      toast.error('Guarde la cotización primero antes de ver la vista previa');
      return;
    }
    
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Verificar que el blob es válido
      if (!response.data || response.data.size === 0) {
        throw new Error('PDF vacío');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPreview(true);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Cotización no encontrada. Guárdela primero.');
      } else if (error.response?.status === 500) {
        toast.error('Error interno al generar el PDF. Verifique los datos.');
      } else {
        toast.error('Error al cargar la vista previa. Intente de nuevo.');
      }
    } finally {
      setLoadingPreview(false);
    }
  };

  // Cerrar vista previa
  const handleClosePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Convertir a pedido
  const handleConvertToOrder = async () => {
    // Si el estado actual en el form es 'aprobada' pero hay cambios sin guardar, guardar primero
    if (quote.estado === 'aprobada') {
      try {
        const token = localStorage.getItem('token');
        
        // Primero guardar el estado actual
        await axios.put(`${API_URL}/api/quotes/${id}`, {
          ...quote,
          estado: 'aprobada',
          items: quote.items.map(item => ({
            ...item,
            precio_cliente: item.precio_base * item.cantidad
          }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Luego convertir a pedido
        const response = await axios.post(`${API_URL}/api/quotes/${id}/convert-to-order`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cotización convertida a pedido');
        navigate(`/sheet/${response.data.sheet_id}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al convertir a pedido');
      }
    } else {
      toast.error('Primero cambia el estado a "Aprobada" y guarda');
    }
  };

  // Obtener descripción del item para mostrar en tabla
  const getItemDescription = (item) => {
    // Siempre usar descripcion_manual si existe
    if (item.descripcion_manual) {
      return item.descripcion_manual;
    }
    // Items del Cotizador nuevo - usar label descriptivo si existe
    if (item.tipo_item === 'cotizador' && item.confeccion) {
      const confeccionDisplay = item.confeccion_label || getConfeccionLabel(item.confeccion);
      return `${confeccionDisplay} - ${item.talla || 'Sin talla'} ${item.express === 'SÍ' ? '(Express)' : ''}`;
    }
    // Fallback para items antiguos - NO mostrar marca/referencia
    if (item.tipo_item === 'lista_precios' || (!item.tipo_item && item.marca)) {
      const prenda = TIPO_PRENDA_LABELS[item.tipo_prenda] || item.tipo_prenda;
      // Solo mostrar el tipo de prenda, NO la marca ni referencia
      return `${prenda} - Prenda a la medida`;
    }
    return item.nombre || item.descripcion || 'Item';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/quotes')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Volver</span>
              </button>
              <div className="h-6 w-px bg-white/20" />
              <h1 className="font-display text-lg">
                {isEditing ? `Cotización ${quote.numero || ''}` : 'Nueva Cotización'}
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing && (
                <>
                  <button
                    onClick={handlePreviewPDF}
                    disabled={loadingPreview}
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Vista previa"
                  >
                    {loadingPreview ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline text-sm">Vista previa</span>
                  </button>
                  <button
                    onClick={handleSendWhatsApp}
                    disabled={sendingWhatsApp}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-wait rounded-lg transition-colors"
                    title="Enviar por WhatsApp"
                  >
                    {sendingWhatsApp ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <MessageCircle className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline text-sm">{sendingWhatsApp ? 'Preparando...' : 'WhatsApp'}</span>
                  </button>
                  <button
                    onClick={handleDuplicate}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Duplicar"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Descargar PDF"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </>
              )}
              <button
                onClick={() => handleSave()}
                disabled={saving}
                data-testid="quote-guardar-btn"
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Layout principal: 70% formulario, 30% resumen (max 320px) */}
        <div className="flex flex-col xl:flex-row gap-8">
          {/* Columna principal - Formulario (flex-grow para tomar el espacio disponible) */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Banner de cliente vinculado */}
            {quote.cliente_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Cliente vinculado: {quote.cliente_id}</p>
                    <p className="text-xs text-emerald-600">Los datos están sincronizados con el historial del cliente</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={desvincularCliente}
                  className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                >
                  Desvincular
                </button>
              </div>
            )}
            
            {/* Banner de Puntos JPM disponibles */}
            {clientePuntos && clientePuntos.puntos_disponibles > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-[#D4AF37]/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-900">
                        Este cliente tiene {clientePuntos.puntos_disponibles} Puntos JPM
                      </p>
                      <p className="text-xs text-amber-700">
                        Beneficio disponible: ${clientePuntos.valor_redencion?.toLocaleString('es-CO')} COP
                      </p>
                    </div>
                  </div>
                  
                  {quote.puntos_jpm_a_redimir > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-emerald-100 px-3 py-1.5 rounded-lg">
                        <p className="text-xs text-emerald-800 font-medium">
                          Aplicados: {quote.puntos_jpm_a_redimir} pts (-${quote.puntos_jpm_descuento?.toLocaleString('es-CO')})
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={quitarPuntosJPM}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Quitar
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setPuntosARedimir(clientePuntos.puntos_disponibles);
                        setShowPuntosModal(true);
                      }}
                      className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C49F27] text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Gift className="w-4 h-4" />
                      Aplicar Puntos
                    </button>
                  )}
                </div>
              </div>
            )}
            <BannerBeneficioReferido beneficio={beneficioRef.beneficio} aplicado={beneficioRef.aplicado} onQuitar={beneficioRef.quitar}
              onAplicar={beneficioRef.aplicar} guardado={isEditing ? quote.beneficio_referido : null} aviso={isEditing ? null : beneficioRef.aviso} />
            
            {/* Datos del cliente */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h2 className="font-display text-xl mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Datos del Cliente
              </h2>
              
              {/* Fila de documento - NUEVA */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <CreditCard className="w-3 h-3 inline mr-1" />
                    Tipo Documento
                  </label>
                  <select
                    value={quote.cliente_tipo_documento || 'CC'}
                    onChange={(e) => setQuote(prev => ({ ...prev, cliente_tipo_documento: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                    data-testid="tipo-documento-select"
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="NIT">NIT</option>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Número de Documento
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={quote.cliente_documento || ''}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setQuote(prev => ({ ...prev, cliente_documento: valor }));
                      }}
                      onBlur={(e) => {
                        const valor = e.target.value;
                        if (valor.length >= 5) {
                          buscarClientePorDocumento(quote.cliente_tipo_documento || 'CC', valor);
                        }
                      }}
                      className="flex-1 px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                      placeholder="Número de documento"
                      data-testid="numero-documento-input"
                    />
                    {buscandoCliente && (
                      <div className="flex items-center px-3">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Al escribir el documento, verificamos si el cliente ya existe en el sistema
                  </p>
                </div>
              </div>
              
              {/* Selector de Moneda */}
              <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
                <label className="block text-xs uppercase tracking-wider text-emerald-700 mb-2 font-medium">
                  💰 Moneda de la Cotización
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.values(MONEDAS).map((m) => (
                    <button
                      key={m.codigo}
                      type="button"
                      onClick={() => setQuote(prev => ({ ...prev, moneda: m.codigo }))}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                        quote.moneda === m.codigo
                          ? 'border-emerald-500 bg-emerald-500 text-white shadow-lg'
                          : 'border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                      data-testid={`moneda-${m.codigo}`}
                    >
                      <span className="text-lg">{m.bandera}</span>
                      <span>{m.codigo}</span>
                      <span className="text-xs opacity-75">({m.simbolo})</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-emerald-600 mt-2">
                  Todos los precios y totales se mostrarán en {MONEDAS[quote.moneda]?.nombre || 'Peso Colombiano'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Nombre del Cliente *
                  </label>
                  <input
                    type="text"
                    value={quote.cliente_nombre || ''}
                    data-testid="quote-cliente-nombre-input"
                    onChange={(e) => setQuote(prev => ({ ...prev, cliente_nombre: e.target.value }))}
                    className="w-full px-4 py-3 border-b border-border bg-transparent focus:outline-none focus:border-primary transition-colors"
                    placeholder="Nombre completo"
                  />
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <Phone className="w-3 h-3 inline mr-1" />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    data-testid="quote-cliente-telefono-input"
                    value={quote.cliente_telefono || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, cliente_telefono: e.target.value }))}
                    onBlur={(e) => {
                      if (!isEditing) {
                        buscarClientePorTelefono(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 border-b border-border bg-transparent focus:outline-none focus:border-primary transition-colors"
                    placeholder="+57 300 123 4567"
                  />
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <Mail className="w-3 h-3 inline mr-1" />
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    value={quote.cliente_correo || ''}
                    data-testid="quote-cliente-correo-input"
                    onChange={(e) => setQuote(prev => ({ ...prev, cliente_correo: e.target.value }))}
                    onBlur={(e) => {
                      if (!isEditing) {
                        buscarClientePorCorreo(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 border-b border-border bg-transparent focus:outline-none focus:border-primary transition-colors"
                    placeholder="cliente@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <Award className="w-3 h-3 inline mr-1" />
                    Vendedor Responsable
                  </label>
                  <select
                    value={quote.vendedor_id || ''}
                    onChange={(e) => {
                      const vendedorId = e.target.value;
                      const vendedor = vendedoresActivos.find(v => v.id === vendedorId);
                      setQuote(prev => ({
                        ...prev,
                        vendedor_id: vendedorId,
                        vendedor_codigo: vendedor?.id_vendedor || '',
                        vendedor_nombre: vendedor?.nombre || ''
                      }));
                    }}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                    data-testid="vendedor-select"
                  >
                    <option value="">Seleccionar vendedor...</option>
                    {vendedoresActivos.map(vendedor => (
                      <option key={vendedor.id} value={vendedor.id}>
                        {`${vendedor.nombre} (${vendedor.id_vendedor})`}
                      </option>
                    ))}
                  </select>
                  {quote.vendedor_codigo && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Vendedor asignado: {quote.vendedor_codigo}
                    </p>
                  )}
                </div>
                
                {/* Fecha del Evento - OBLIGATORIA */}
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Fecha del Evento *
                  </label>
                  <input
                    type="date"
                    value={quote.fecha_evento || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, fecha_evento: e.target.value, plan_pagos: [] }))}
                    className="w-full px-4 py-3 border-b border-border bg-transparent focus:outline-none focus:border-primary transition-colors"
                    data-testid="fecha-evento-input"
                    required
                  />
                  {quote.fecha_evento && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Entrega estimada: {formatDateDisplay(new Date(new Date(quote.fecha_evento).getTime() - 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])} (8 días antes)
                    </p>
                  )}
                  {!quote.fecha_evento && (
                    <p className="text-xs text-amber-600 mt-1">
                      La fecha del evento es obligatoria para crear la cotización
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Agregar Item - Selector de Tipo */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h2 className="font-display text-xl mb-6 flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Agregar Item
              </h2>
              
              {/* Tabs de tipo de item */}
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(TIPOS_ITEM).map(([key, { label, icon: Icon, color }]) => (
                  <button
                    key={key}
                    onClick={() => setTipoItem(key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      tipoItem === key 
                        ? 'bg-black text-white' 
                        : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Formulario según tipo de item */}
              {/* NUEVO ORDEN UX: TIPO → CONFECCIÓN → TALLA → EXPRESS → CANTIDAD → DTO% → PAÑO/REFERENCIA → PRECIO → AGREGAR */}
              {/* Permite comparar diferentes telas sin reiniciar el flujo completo */}
              {/* Solo muestra opciones con precio > 0 para evitar confusión */}
              {/* OPTIMIZADO: Usa catalog.precios (mapa compacto) en lugar de catalog.tarifas (array) */}
              {tipoItem === 'cotizador' && catalog.precios && getTiposConPrecio().length > 0 && (
                <div className="space-y-5">
                  {/* Primera fila: Tipo y Confección/Prenda */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Tipo *</label>
                      <select
                        value={newItemCotizador.tipo}
                        onChange={(e) => setNewItemCotizador(prev => ({ 
                          ...prev, 
                          tipo: e.target.value, 
                          confeccion: '', 
                          talla: '', 
                          express: '',
                          pano_referencia: '', 
                          precio_base: 0 
                        }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        data-testid="select-tipo-cotizador"
                      >
                        <option value="">Seleccionar tipo</option>
                        {getTiposConPrecio().map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Confección / Prenda *</label>
                      <select
                        value={newItemCotizador.confeccion}
                        onChange={(e) => setNewItemCotizador(prev => ({ 
                          ...prev, 
                          confeccion: e.target.value, 
                          talla: '', 
                          express: '',
                          pano_referencia: '', 
                          precio_base: 0 
                        }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        disabled={!newItemCotizador.tipo}
                        data-testid="select-confeccion"
                      >
                        <option value="">Seleccionar confección</option>
                        {getConfeccionesPorTipo(newItemCotizador.tipo).map(conf => (
                          <option key={conf} value={conf}>{getConfeccionLabel(conf)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Segunda fila: Talla y Express */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Talla *</label>
                      <select
                        value={newItemCotizador.talla}
                        onChange={(e) => setNewItemCotizador(prev => ({ 
                          ...prev, 
                          talla: e.target.value, 
                          express: '',
                          pano_referencia: '', 
                          precio_base: 0 
                        }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        disabled={!newItemCotizador.confeccion}
                        data-testid="select-talla"
                      >
                        <option value="">Seleccionar talla</option>
                        {getTallasParaTipoConfeccion(newItemCotizador.tipo, newItemCotizador.confeccion).map(talla => (
                          <option key={talla} value={talla}>{talla}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Express *</label>
                      <select
                        value={newItemCotizador.express}
                        onChange={(e) => setNewItemCotizador(prev => ({ 
                          ...prev, 
                          express: e.target.value,
                          pano_referencia: '', 
                          precio_base: 0 
                        }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        disabled={!newItemCotizador.talla}
                        data-testid="select-express"
                      >
                        <option value="">Seleccionar</option>
                        {getExpressParaTipoConfeccionTalla(newItemCotizador.tipo, newItemCotizador.confeccion, newItemCotizador.talla).map(exp => (
                          <option key={exp} value={exp}>{exp}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Tercera fila: Cantidad y Descuento */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={newItemCotizador.cantidad}
                        onChange={(e) => setNewItemCotizador(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm text-center"
                        data-testid="input-cantidad-cotizador"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Dto. % {descuentoConfig.es_gerencia && <span className="text-emerald-600">(hasta 100%)</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={descuentoConfig.descuento_maximo_porcentaje}
                        value={newItemCotizador.descuento_porcentaje}
                        onChange={(e) => {
                          const valor = parseInt(e.target.value) || 0;
                          const valorLimitado = Math.min(valor, descuentoConfig.descuento_maximo_porcentaje);
                          if (valor > descuentoConfig.descuento_maximo_porcentaje) {
                            toast.error(`Tu rol solo permite descuentos hasta ${descuentoConfig.descuento_maximo_porcentaje}%`);
                          }
                          setNewItemCotizador(prev => ({ ...prev, descuento_porcentaje: valorLimitado }));
                        }}
                        className={`w-full px-3 py-2.5 border rounded-lg bg-white focus:outline-none text-sm text-center ${
                          newItemCotizador.descuento_porcentaje === 100 ? 'border-emerald-500 bg-emerald-50' : 'border-border focus:border-primary'
                        }`}
                        placeholder="0"
                        data-testid="input-descuento-cotizador"
                        title={`Máximo permitido: ${descuentoConfig.descuento_maximo_porcentaje}%`}
                      />
                    </div>
                    
                    {/* Espacio vacío para alinear */}
                    <div className="hidden sm:block"></div>
                    <div className="hidden sm:block"></div>
                  </div>
                  
                  {/* Cuarta fila: PAÑO / REFERENCIA / TELA - ÚLTIMA SELECCIÓN para comparar telas fácilmente */}
                  <div className="pt-3 border-t border-border">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
                        Último paso: seleccione la tela para ver el precio
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Paño / Referencia / Tela *</label>
                        <select
                          value={newItemCotizador.pano_referencia}
                          onChange={(e) => setNewItemCotizador(prev => ({ 
                            ...prev, 
                            pano_referencia: e.target.value
                            // NO se limpia ningún otro campo - solo se recalcula el precio automáticamente
                          }))}
                          className="w-full px-3 py-2.5 border border-amber-300 rounded-lg bg-amber-50/50 focus:outline-none focus:border-amber-500 text-sm"
                          disabled={!newItemCotizador.express}
                          data-testid="select-referencia-cotizador"
                        >
                          <option value="">Seleccionar paño / referencia / tela</option>
                          {getReferenciasFiltradas(newItemCotizador.tipo, newItemCotizador.confeccion, newItemCotizador.talla, newItemCotizador.express).map(ref => (
                            <option key={ref} value={ref}>{normalizarLabelTela(ref)}</option>
                          ))}
                        </select>
                        {newItemCotizador.express && !newItemCotizador.pano_referencia && (
                          <p className="text-xs text-amber-600 mt-1">
                            {getReferenciasFiltradas(newItemCotizador.tipo, newItemCotizador.confeccion, newItemCotizador.talla, newItemCotizador.express).length} telas disponibles para esta combinación
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <button
                          onClick={handleAddItemCotizador}
                          disabled={!newItemCotizador.precio_base || newItemCotizador.precio_base <= 0}
                          className="w-full flex items-center justify-center gap-2 bg-black text-white px-6 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="btn-agregar-cotizador"
                        >
                          <Plus className="w-4 h-4" />
                          Agregar
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Precio del Cotizador (solo lectura) - Convertido a la moneda de la cotización */}
                  {newItemCotizador.precio_base > 0 && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="precio-cotizador-box">
                      <p className="text-sm text-emerald-800">
                        <Calculator className="w-4 h-4 inline mr-1" />
                        Precio unitario: <strong data-testid="precio-unitario-cotizador">{fp(convertirPrecioCotizador(newItemCotizador.precio_base))}</strong>
                        {quote.moneda !== 'COP' && (
                          <span className="text-xs ml-2 text-emerald-600/70">
                            (Base COP: {formatPrice(newItemCotizador.precio_base, 'COP')})
                          </span>
                        )}
                        <span className="text-xs ml-2 text-emerald-600">
                          ({newItemCotizador.tipo} · {getConfeccionLabel(newItemCotizador.confeccion)} · {newItemCotizador.talla} · Express: {newItemCotizador.express})
                        </span>
                      </p>
                    </div>
                  )}
                  
                  {/* Campo de Descripción Manual OBLIGATORIO */}
                  <div className="border-t border-border pt-4">
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Descripción para el Cliente * <span className="text-blue-600 font-normal normal-case">(Este texto aparecerá en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemCotizador.descripcion_manual}
                      onChange={(e) => setNewItemCotizador(prev => ({ ...prev, descripcion_manual: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Traje de novio en paño italiano azul noche, construcción sastrera, solapa clásica, terminaciones artesanales y ajuste personalizado."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Escriba la descripción comercial que verá el cliente. El precio viene del Cotizador.
                    </p>
                  </div>
                  
                  {/* Notas internas - NO aparecen en PDF del cliente */}
                  <div className="border-t border-amber-200 pt-4 bg-amber-50/50 rounded-lg p-3 mt-2">
                    <label className="block text-xs uppercase tracking-wider text-amber-700 mb-2">
                      Notas Internas <span className="text-amber-600 font-normal normal-case">(Solo uso interno - NO aparece en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemCotizador.notas_internas || ''}
                      onChange={(e) => setNewItemCotizador(prev => ({ ...prev, notas_internas: e.target.value }))}
                      className="w-full px-3 py-2 border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-500 text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Cliente prefiere esta tela pero pidió revisar alternativa más oscura. Confirmar disponibilidad."
                    />
                  </div>
                </div>
              )}

              {/* Formulario LEGACY (solo si NO hay tarifas nuevas cargadas) */}
              {tipoItem === 'cotizador' && (!catalog.tipos || catalog.tipos.length === 0) && catalog.marcas && catalog.marcas.length > 0 && (
                <div className="space-y-5">
                  {/* Primera fila: Marca, Referencia, Prenda */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Marca *</label>
                      <select
                        value={newItemListaPrecios.marca}
                        onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, marca: e.target.value, coleccion: '', referencia: '' }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        data-testid="select-marca"
                      >
                        <option value="">Seleccionar marca</option>
                        {catalog.marcas.map(marca => (
                          <option key={marca} value={marca}>{marca}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Referencia *</label>
                      <select
                        value={newItemListaPrecios.referencia}
                        onChange={(e) => {
                          const ref = e.target.value;
                          const autoColeccion = !newItemListaPrecios.coleccion && ref 
                            ? getColeccionForReferencia(newItemListaPrecios.marca, ref)
                            : newItemListaPrecios.coleccion;
                          setNewItemListaPrecios(prev => ({ 
                            ...prev, 
                            referencia: ref,
                            coleccion: autoColeccion || prev.coleccion
                          }));
                        }}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        disabled={!newItemListaPrecios.marca}
                        data-testid="select-referencia"
                      >
                        <option value="">Seleccionar referencia</option>
                        {getReferencias(newItemListaPrecios.marca, newItemListaPrecios.coleccion).map(ref => (
                          <option key={ref} value={ref}>{ref}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Prenda *</label>
                      <select
                        value={newItemListaPrecios.tipo_prenda}
                        onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, tipo_prenda: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        data-testid="select-prenda"
                      >
                        <option value="">Seleccionar prenda</option>
                        {catalog.tipos_prenda.map(tipo => (
                          <option key={tipo} value={tipo}>{TIPO_PRENDA_LABELS[tipo]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Segunda fila: Cantidad, Descuento, Botón Agregar */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={newItemListaPrecios.cantidad}
                        onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm text-center"
                        data-testid="input-cantidad"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Dto. % {descuentoConfig.es_gerencia && <span className="text-emerald-600">(hasta 100%)</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={descuentoConfig.descuento_maximo_porcentaje}
                        value={newItemListaPrecios.descuento_porcentaje}
                        onChange={(e) => {
                          const valor = parseInt(e.target.value) || 0;
                          const valorLimitado = Math.min(valor, descuentoConfig.descuento_maximo_porcentaje);
                          if (valor > descuentoConfig.descuento_maximo_porcentaje) {
                            toast.error(`Tu rol solo permite descuentos hasta ${descuentoConfig.descuento_maximo_porcentaje}%`);
                          }
                          setNewItemListaPrecios(prev => ({ ...prev, descuento_porcentaje: valorLimitado }));
                        }}
                        className={`w-full px-3 py-2.5 border rounded-lg bg-white focus:outline-none text-sm text-center ${
                          newItemListaPrecios.descuento_porcentaje === 100 ? 'border-emerald-500 bg-emerald-50' : 'border-border focus:border-primary'
                        }`}
                        placeholder="0"
                        data-testid="input-descuento"
                        title={`Máximo permitido: ${descuentoConfig.descuento_maximo_porcentaje}%`}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <button
                        onClick={handleAddItemListaPrecios}
                        className="w-full flex items-center justify-center gap-2 bg-black text-white px-6 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors text-sm font-medium"
                        data-testid="btn-agregar-item"
                      >
                        <Plus className="w-4 h-4" />
                        Agregar
                      </button>
                    </div>
                  </div>
                  
                  {/* Filtro opcional de colección (colapsado) */}
                  {newItemListaPrecios.marca && (
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">Filtrar por colección:</span>
                      <select
                        value={newItemListaPrecios.coleccion}
                        onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, coleccion: e.target.value, referencia: '' }))}
                        className="px-3 py-1.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                      >
                        <option value="">Todas</option>
                        {getColeccionesPorMarca(newItemListaPrecios.marca).map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Campo de Descripción Manual OBLIGATORIO */}
                  <div className="border-t border-border pt-4">
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Descripción para el Cliente * <span className="text-blue-600 font-normal normal-case">(Este texto aparecerá en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemListaPrecios.descripcion_manual}
                      onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, descripcion_manual: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Traje de novio en paño italiano azul noche, construcción sastrera, solapa clásica, terminaciones artesanales y ajuste personalizado."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Escriba la descripción comercial que verá el cliente. La lista de precios solo alimenta el precio.
                    </p>
                  </div>
                  
                  {newItemListaPrecios.marca && newItemListaPrecios.referencia && newItemListaPrecios.tipo_prenda && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <Calculator className="w-4 h-4 inline mr-1" />
                        Precio unitario: <strong>{fp(getPrice(newItemListaPrecios.marca, newItemListaPrecios.coleccion, newItemListaPrecios.referencia, newItemListaPrecios.tipo_prenda))}</strong>
                        <span className="text-xs ml-2 text-blue-600">({newItemListaPrecios.marca}{newItemListaPrecios.coleccion ? ` - ${newItemListaPrecios.coleccion}` : ''})</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Notas internas - NO aparecen en PDF del cliente */}
                  <div className="border-t border-amber-200 pt-4 bg-amber-50/50 rounded-lg p-3 mt-2">
                    <label className="block text-xs uppercase tracking-wider text-amber-700 mb-2">
                      Notas Internas <span className="text-amber-600 font-normal normal-case">(Solo uso interno - NO aparece en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemListaPrecios.notas_internas || ''}
                      onChange={(e) => setNewItemListaPrecios(prev => ({ ...prev, notas_internas: e.target.value }))}
                      className="w-full px-3 py-2 border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-500 text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Cliente prefiere esta tela pero pidió revisar alternativa más oscura. Confirmar disponibilidad."
                    />
                  </div>
                </div>
              )}

              {/* Mensaje cuando el catálogo no está disponible */}
              {tipoItem === 'cotizador' && (!catalog.tipos || catalog.tipos.length === 0) && (!catalog.marcas || catalog.marcas.length === 0) && (
                <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-amber-800 font-medium">Cargando catálogo de precios...</p>
                  <p className="text-amber-600 text-sm mt-1">Si tarda demasiado, intente refrescar la página (Ctrl+Shift+R)</p>
                </div>
              )}

              {tipoItem === 'manual' && (
                <div className="space-y-5">
                  {/* Primera fila: Nombre y Precio */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Nombre del Producto * <span className="text-muted-foreground/60 font-normal normal-case">(uso interno)</span></label>
                      <input
                        type="text"
                        value={newItemManual.nombre}
                        onChange={(e) => setNewItemManual(prev => ({ ...prev, nombre: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        placeholder="Ej: Camisa, Mancornas, Zapatos"
                        list="accesorios-sugeridos"
                        data-testid="input-nombre-manual"
                      />
                      <datalist id="accesorios-sugeridos">
                        {catalog.accesorios_sugeridos?.map(acc => (
                          <option key={acc} value={acc} />
                        ))}
                      </datalist>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Precio Unitario *</label>
                      <input
                        type="number"
                        min="0"
                        value={newItemManual.precio_base}
                        onChange={(e) => setNewItemManual(prev => ({ ...prev, precio_base: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        placeholder="450000"
                        data-testid="input-precio-manual"
                      />
                    </div>
                  </div>
                  
                  {/* Segunda fila: Cantidad, Descuento, Botón */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={newItemManual.cantidad}
                        onChange={(e) => setNewItemManual(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm text-center"
                        data-testid="input-cantidad-manual"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Dto. % {descuentoConfig.es_gerencia && <span className="text-emerald-600">(hasta 100%)</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={descuentoConfig.descuento_maximo_porcentaje}
                        value={newItemManual.descuento_porcentaje}
                        onChange={(e) => {
                          const valor = parseInt(e.target.value) || 0;
                          const valorLimitado = Math.min(valor, descuentoConfig.descuento_maximo_porcentaje);
                          if (valor > descuentoConfig.descuento_maximo_porcentaje) {
                            toast.error(`Tu rol solo permite descuentos hasta ${descuentoConfig.descuento_maximo_porcentaje}%`);
                          }
                          setNewItemManual(prev => ({ ...prev, descuento_porcentaje: valorLimitado }));
                        }}
                        className={`w-full px-3 py-2.5 border rounded-lg bg-white focus:outline-none text-sm text-center ${
                          newItemManual.descuento_porcentaje === 100 ? 'border-emerald-500 bg-emerald-50' : 'border-border focus:border-primary'
                        }`}
                        placeholder="0"
                        data-testid="input-descuento-manual"
                        title={`Máximo permitido: ${descuentoConfig.descuento_maximo_porcentaje}%`}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <button
                        onClick={handleAddItemManual}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-2.5 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                        data-testid="btn-agregar-manual"
                      >
                        <Plus className="w-4 h-4" />
                        Agregar
                      </button>
                    </div>
                  </div>
                  
                  {/* Campo de Descripción Manual OBLIGATORIO */}
                  <div className="border-t border-border pt-4">
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Descripción para el Cliente * <span className="text-purple-600 font-normal normal-case">(Este texto aparecerá en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemManual.descripcion_manual}
                      onChange={(e) => setNewItemManual(prev => ({ ...prev, descripcion_manual: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Camisa blanca a la medida en tela italiana, cuello italiano, puño sencillo y corte slim fit."
                    />
                  </div>

                  {newItemManual.precio_base > 0 && (
                    <div className={`p-3 border rounded-lg ${newItemManual.descuento_porcentaje === 100 ? 'bg-emerald-50 border-emerald-200' : 'bg-purple-50 border-purple-200'}`}>
                      <p className={`text-sm ${newItemManual.descuento_porcentaje === 100 ? 'text-emerald-800' : 'text-purple-800'}`}>
                        <Calculator className="w-4 h-4 inline mr-1" />
                        {newItemManual.descuento_porcentaje === 100 ? (
                          <>
                            Total: <strong className="line-through text-zinc-400">{fp(newItemManual.precio_base * newItemManual.cantidad)}</strong>
                            <strong className="ml-2 text-emerald-600">$0 (Cortesía)</strong>
                          </>
                        ) : newItemManual.descuento_porcentaje > 0 ? (
                          <>
                            Total: <strong className="line-through text-zinc-400">{fp(newItemManual.precio_base * newItemManual.cantidad)}</strong>
                            <strong className="ml-2">{fp(Math.round(newItemManual.precio_base * newItemManual.cantidad * (1 - newItemManual.descuento_porcentaje / 100)))}</strong>
                            <span className="text-xs ml-1">(-{newItemManual.descuento_porcentaje}%)</span>
                          </>
                        ) : (
                          <>Total: <strong>{fp(newItemManual.precio_base * newItemManual.cantidad)}</strong></>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* Referencia de material y Notas internas */}
                  <div className="border-t border-amber-200 pt-4 bg-amber-50/50 rounded-lg p-3 mt-2 space-y-3">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-amber-700 mb-2">
                        Referencia / Material <span className="text-amber-600 font-normal normal-case">(Solo uso interno)</span>
                      </label>
                      <input
                        type="text"
                        value={newItemManual.referencia_material || ''}
                        onChange={(e) => setNewItemManual(prev => ({ ...prev, referencia_material: e.target.value }))}
                        className="w-full px-3 py-2 border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-500 text-sm"
                        placeholder="Ej: Tela ref 130245 Azul italiano, Cuero negro"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-amber-700 mb-2">
                        Notas Internas <span className="text-amber-600 font-normal normal-case">(NO aparece en el PDF)</span>
                      </label>
                      <textarea
                        value={newItemManual.notas_internas || ''}
                        onChange={(e) => setNewItemManual(prev => ({ ...prev, notas_internas: e.target.value }))}
                        className="w-full px-3 py-2 border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-500 text-sm resize-none"
                        rows={2}
                        placeholder="Ej: Confirmar disponibilidad antes de producción."
                      />
                    </div>
                  </div>
                </div>
              )}

              {tipoItem === 'servicio' && (
                <div className="space-y-5">
                  {/* Primera fila: Nombre y Precio */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Nombre del Servicio * <span className="text-muted-foreground/60 font-normal normal-case">(uso interno)</span></label>
                      <input
                        type="text"
                        value={newItemServicio.nombre}
                        onChange={(e) => setNewItemServicio(prev => ({ ...prev, nombre: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        placeholder="Ej: Envío, Urgencia, Ajuste"
                        list="servicios-sugeridos"
                        data-testid="input-nombre-servicio"
                      />
                      <datalist id="servicios-sugeridos">
                        {catalog.servicios_sugeridos?.map(serv => (
                          <option key={serv} value={serv} />
                        ))}
                      </datalist>
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Precio *</label>
                      <input
                        type="number"
                        min="0"
                        value={newItemServicio.precio_base}
                        onChange={(e) => setNewItemServicio(prev => ({ ...prev, precio_base: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                        placeholder="50000"
                        data-testid="input-precio-servicio"
                      />
                    </div>
                  </div>
                  
                  {/* Segunda fila: Cantidad, Descuento, Botón */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={newItemServicio.cantidad}
                        onChange={(e) => setNewItemServicio(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm text-center"
                        data-testid="input-cantidad-servicio"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Dto. % {descuentoConfig.es_gerencia && <span className="text-emerald-600">(hasta 100%)</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={descuentoConfig.descuento_maximo_porcentaje}
                        value={newItemServicio.descuento_porcentaje}
                        onChange={(e) => {
                          const valor = parseInt(e.target.value) || 0;
                          const valorLimitado = Math.min(valor, descuentoConfig.descuento_maximo_porcentaje);
                          if (valor > descuentoConfig.descuento_maximo_porcentaje) {
                            toast.error(`Tu rol solo permite descuentos hasta ${descuentoConfig.descuento_maximo_porcentaje}%`);
                          }
                          setNewItemServicio(prev => ({ ...prev, descuento_porcentaje: valorLimitado }));
                        }}
                        className={`w-full px-3 py-2.5 border rounded-lg bg-white focus:outline-none text-sm text-center ${
                          newItemServicio.descuento_porcentaje === 100 ? 'border-emerald-500 bg-emerald-50' : 'border-border focus:border-primary'
                        }`}
                        placeholder="0"
                        data-testid="input-descuento-servicio"
                        title={`Máximo permitido: ${descuentoConfig.descuento_maximo_porcentaje}%`}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <button
                        onClick={handleAddItemServicio}
                        className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        data-testid="btn-agregar-servicio"
                      >
                        <Plus className="w-4 h-4" />
                        Agregar
                      </button>
                    </div>
                  </div>
                  
                  {/* Campo de Descripción Manual OBLIGATORIO */}
                  <div className="border-t border-border pt-4">
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Descripción para el Cliente * <span className="text-green-600 font-normal normal-case">(Este texto aparecerá en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemServicio.descripcion_manual}
                      onChange={(e) => setNewItemServicio(prev => ({ ...prev, descripcion_manual: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-white focus:outline-none focus:border-primary text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Envío nacional asegurado a Medellín, entrega puerta a puerta."
                    />
                  </div>

                  {newItemServicio.precio_base > 0 && (
                    <div className={`p-3 border rounded-lg ${newItemServicio.descuento_porcentaje === 100 ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'}`}>
                      <p className={`text-sm ${newItemServicio.descuento_porcentaje === 100 ? 'text-emerald-800' : 'text-green-800'}`}>
                        <Calculator className="w-4 h-4 inline mr-1" />
                        {newItemServicio.descuento_porcentaje === 100 ? (
                          <>
                            Total: <strong className="line-through text-zinc-400">{fp(newItemServicio.precio_base * newItemServicio.cantidad)}</strong>
                            <strong className="ml-2 text-emerald-600">$0 (Cortesía)</strong>
                          </>
                        ) : newItemServicio.descuento_porcentaje > 0 ? (
                          <>
                            Total: <strong className="line-through text-zinc-400">{fp(newItemServicio.precio_base * newItemServicio.cantidad)}</strong>
                            <strong className="ml-2">{fp(Math.round(newItemServicio.precio_base * newItemServicio.cantidad * (1 - newItemServicio.descuento_porcentaje / 100)))}</strong>
                            <span className="text-xs ml-1">(-{newItemServicio.descuento_porcentaje}%)</span>
                          </>
                        ) : (
                          <>Total: <strong>{fp(newItemServicio.precio_base * newItemServicio.cantidad)}</strong></>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* Notas internas para servicios */}
                  <div className="border-t border-amber-200 pt-4 bg-amber-50/50 rounded-lg p-3 mt-2">
                    <label className="block text-xs uppercase tracking-wider text-amber-700 mb-2">
                      Notas Internas <span className="text-amber-600 font-normal normal-case">(NO aparece en el PDF)</span>
                    </label>
                    <textarea
                      value={newItemServicio.notas_internas || ''}
                      onChange={(e) => setNewItemServicio(prev => ({ ...prev, notas_internas: e.target.value }))}
                      className="w-full px-3 py-2 border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-500 text-sm resize-none"
                      rows={2}
                      placeholder="Ej: Coordinar con mensajería antes del viernes."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Tabla de Items */}
            <div className="bg-white rounded-xl border border-border overflow-hidden">
              <div className="p-6 border-b border-border">
                <h2 className="font-display text-xl flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Detalle de la Cotización
                </h2>
              </div>
              
              {quote.items.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No hay items en la cotización</p>
                  <p className="text-sm">Agregue productos, accesorios o servicios</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">#</th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Descripción</th>
                        <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-muted-foreground">Precio Unit.</th>
                        <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground">Cant.</th>
                        <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground">Dto. %</th>
                        <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-muted-foreground">Total</th>
                        <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {quote.items.map((item, index) => {
                        const tipoInfo = TIPOS_ITEM[item.tipo_item] || TIPOS_ITEM.cotizador;
                        const TipoIcon = tipoInfo.icon;
                        const tieneInfoInterna = item.referencia_interna || item.notas_internas || item.referencia_material;
                        const valorBase = item.precio_base * item.cantidad;
                        const descuentoItem = item.descuento_porcentaje || 0;
                        const totalItem = calcularTotalItem(item);
                        
                        return (
                          <React.Fragment key={item.id}>
                            <tr className="hover:bg-zinc-50 transition-colors">
                              <td className="px-4 py-4 text-sm">{index + 1}</td>
                              <td className="px-4 py-4">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${tipoInfo.color}`}>
                                  <TipoIcon className="w-3 h-3" />
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm">
                                {getItemDescription(item)}
                                {tieneInfoInterna && (
                                  <span className="ml-2 text-amber-600 text-xs" title="Tiene información interna">
                                    📋
                                  </span>
                                )}
                                {descuentoItem === 100 && (
                                  <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                    Cortesía
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-4 text-sm text-right">{fp(item.precio_base)}</td>
                              <td className="px-4 py-4">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.cantidad}
                                  onChange={(e) => handleUpdateQuantity(item.id, parseInt(e.target.value) || 1)}
                                  className="w-16 px-2 py-1 text-center border border-border rounded bg-white focus:outline-none focus:border-primary"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center justify-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={descuentoItem}
                                    onChange={(e) => handleUpdateItemDiscount(item.id, e.target.value)}
                                    className={`w-14 px-2 py-1 text-center border rounded bg-white focus:outline-none focus:border-primary ${
                                      descuentoItem === 100 ? 'border-emerald-500 bg-emerald-50' : 'border-border'
                                    }`}
                                  />
                                  <span className="text-xs text-muted-foreground">%</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-right font-medium">
                                {totalItem === 0 ? (
                                  <span className="text-emerald-600">$0</span>
                                ) : (
                                  fp(totalItem)
                                )}
                                {descuentoItem > 0 && descuentoItem < 100 && (
                                  <div className="text-xs text-red-500">
                                    -{fp(valorBase - totalItem)}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                            {/* Fila de información interna - Solo visible internamente */}
                            {tieneInfoInterna && (
                              <tr className="bg-amber-50/50">
                                <td colSpan="8" className="px-4 py-3">
                                  <div className="flex items-start gap-3 text-xs">
                                    <span className="text-amber-600 font-medium">📋 Info interna:</span>
                                    <div className="flex-1 space-y-1">
                                      {item.referencia_interna && (
                                        <p className="text-amber-800">
                                          {item.referencia_interna.tipo === 'cotizador' ? (
                                            <>
                                              <strong>Tela:</strong> {item.referencia_interna.pano_referencia} · 
                                              <strong> Talla:</strong> {item.referencia_interna.talla} · 
                                              <strong> Express:</strong> {item.referencia_interna.express}
                                            </>
                                          ) : (
                                            <>
                                              <strong>Ref. cotizada:</strong> {item.referencia_interna.marca} - {item.referencia_interna.referencia_tela} ({item.referencia_interna.coleccion || 'Sin colección'})
                                            </>
                                          )}
                                        </p>
                                      )}
                                      {item.referencia_material && (
                                        <p className="text-amber-800">
                                          <strong>Material:</strong> {item.referencia_material}
                                        </p>
                                      )}
                                      {item.notas_internas && (
                                        <p className="text-amber-700 italic">&ldquo;{item.notas_internas}&rdquo;</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Observaciones */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h2 className="font-display text-xl mb-6">Observaciones</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Observaciones Internas
                  </label>
                  <textarea
                    value={quote.observaciones_internas}
                    onChange={(e) => setQuote(prev => ({ ...prev, observaciones_internas: e.target.value }))}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:border-primary resize-none"
                    rows="3"
                    placeholder="Notas internas (no visibles para el cliente)"
                  />
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Observaciones para el Cliente
                  </label>
                  <textarea
                    value={quote.observaciones_cliente}
                    onChange={(e) => setQuote(prev => ({ ...prev, observaciones_cliente: e.target.value }))}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:border-primary resize-none"
                    rows="3"
                    placeholder="Información que aparecerá en el PDF"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Columna lateral - Resumen (ancho fijo, no invade) */}
          <div className="w-full xl:w-80 xl:flex-shrink-0 space-y-6">
            {/* Estado */}
            {isEditing && (
              <div className="bg-white rounded-xl border border-border p-6">
                <h3 className="font-display text-lg mb-4">Estado</h3>
                <select
                  value={quote.estado}
                  onChange={(e) => setQuote(prev => ({ ...prev, estado: e.target.value }))}
                  className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:border-primary"
                >
                  {Object.entries(ESTADOS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                
                {quote.estado === 'aprobada' && (
                  <button
                    onClick={handleConvertToOrder}
                    className="w-full mt-4 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Convertir a Pedido
                  </button>
                )}
              </div>
            )}

            {/* Vigencia */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="font-display text-lg mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Vigencia
              </h3>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Días de vigencia
                </label>
                <input
                  type="number"
                  min="1"
                  value={quote.vigencia_dias}
                  onChange={(e) => setQuote(prev => ({ ...prev, vigencia_dias: parseInt(e.target.value) || 30 }))}
                  className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            {/* Resumen financiero */}
            <div className="bg-black text-white rounded-xl p-6">
              <h3 className="font-display text-lg mb-6">Resumen Inversión</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Items:</span>
                  <span>{quote.items.length}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Subtotal:</span>
                  <span>{fp(subtotal)}</span>
                </div>
                
                {/* Descuento como porcentaje */}
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Descuento:</span>
                  <div className="flex items-center gap-2">
                    <select
                      value={quote.descuento_porcentaje}
                      onChange={(e) => setQuote(prev => ({ ...prev, descuento_porcentaje: parseInt(e.target.value) || 0 }))}
                      className="px-3 py-1 bg-zinc-900 border border-white/20 rounded text-white focus:outline-none focus:border-[#D4AF37] text-sm"
                      data-testid="quote-descuento-general-select"
                    >
                      <option value="0">0%</option>
                      <option value="5">5%</option>
                      <option value="10">10%</option>
                      <option value="15">15%</option>
                      <option value="20">20%</option>
                      {!DESCUENTOS_GENERALES.includes(quote.descuento_porcentaje) && <option value={quote.descuento_porcentaje}>{quote.descuento_porcentaje}%</option>}
                    </select>
                  </div>
                </div>
                
                {/* Valor del descuento en pesos */}
                {descuentoPorcentaje > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/50">Valor descuento:</span>
                    <span className="text-red-400">- {fp(descuentoValor)}</span>
                  </div>
                )}
                
                {/* Descuento de Puntos JPM */}
                {quote.puntos_jpm_descuento > 0 && (
                  <div className="flex justify-between items-center text-sm bg-[#D4AF37]/10 p-2 rounded-lg">
                    <span className="text-[#D4AF37] flex items-center gap-1">
                      <Star className="w-3 h-3" fill="#D4AF37" />
                      Beneficio Puntos JPM:
                    </span>
                    <span className="text-[#D4AF37]">- {fp(quote.puntos_jpm_descuento)}</span>
                  </div>
                )}
                
                <div className="border-t border-white/20 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-medium">Total al Cliente:</span>
                    <span className="text-2xl font-display text-[#D4AF37]">{fp(total)}</span>
                  </div>
                </div>

                {/* Método de Pago */}
                <div className="border-t border-white/20 pt-4">
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Método de Pago
                  </p>
                  
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setQuote(prev => ({ ...prev, metodo_pago: 'normal' }))}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                        quote.metodo_pago === 'normal' 
                          ? 'bg-[#D4AF37] text-black font-medium' 
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      Normal
                    </button>
                    <button
                      onClick={() => setQuote(prev => ({ ...prev, metodo_pago: 'plan_pagos' }))}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                        quote.metodo_pago === 'plan_pagos' 
                          ? 'bg-[#D4AF37] text-black font-medium' 
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      Plan de Pagos
                    </button>
                  </div>

                  {/* Condiciones de pago normal */}
                  {quote.metodo_pago === 'normal' && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Anticipo ({porcentajeAnticipo}%):</span>
                        <span className="text-[#D4AF37]">{fp(anticipoValor)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Saldo ({100 - porcentajeAnticipo}%):</span>
                        <span>{fp(saldoValor)}</span>
                      </div>
                    </div>
                  )}

                  {/* Plan de pagos */}
                  {quote.metodo_pago === 'plan_pagos' && (
                    <div className="space-y-4">
                      
                      {/* SELECTOR DE MODO - Solo para Admin/Gerencia */}
                      {puedeUsarRangoFlexible(userRole) && !quote.modo_plan_pagos && (
                        <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl space-y-4">
                          <div className="text-center">
                            <p className="text-sm text-white font-medium mb-1">¿Cómo desea configurar el plan de pagos?</p>
                            <p className="text-xs text-white/50">Seleccione una opción para continuar</p>
                          </div>
                          
                          <div className="grid grid-cols-1 gap-3">
                            {/* Opción Automático */}
                            <button
                              onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: 'automatico', plan_pagos: [] }))}
                              className="p-4 bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 rounded-xl hover:border-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all text-left group"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Calculator className="w-5 h-5 text-[#D4AF37]" />
                                <span className="text-sm font-medium text-[#D4AF37]">Automático</span>
                              </div>
                              <p className="text-xs text-white/60 leading-relaxed">
                                El sistema calcula las cuotas respetando los mínimos establecidos ($500.000 - $1.000.000 COP)
                              </p>
                            </button>
                            
                            {/* Opción Manual / Libre */}
                            <button
                              onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: 'manual', plan_pagos: [] }))}
                              className="p-4 bg-emerald-500/10 border-2 border-emerald-500/30 rounded-xl hover:border-emerald-500 hover:bg-emerald-500/20 transition-all text-left group"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-5 h-5 text-emerald-400" />
                                <span className="text-sm font-medium text-emerald-400">Semi-Manual</span>
                              </div>
                              <p className="text-xs text-white/60 leading-relaxed">
                                Define la primera cuota y el sistema distribuye el resto automáticamente
                              </p>
                            </button>

                            {/* Opción Manual Completo */}
                            <button
                              onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: 'manual_completo', plan_pagos: [], cuotas_manuales: [] }))}
                              className="p-4 bg-purple-500/10 border-2 border-purple-500/30 rounded-xl hover:border-purple-500 hover:bg-purple-500/20 transition-all text-left group"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-5 h-5 text-purple-400" />
                                <span className="text-sm font-medium text-purple-400">Manual Completo</span>
                              </div>
                              <p className="text-xs text-white/60 leading-relaxed">
                                Define el valor y fecha de cada cuota individualmente
                              </p>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Para usuarios NO privilegiados, ir directo al modo automático */}
                      {!puedeUsarRangoFlexible(userRole) && (
                        <div className="p-3 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-lg">
                          <p className="text-xs text-[#D4AF37] leading-relaxed">
                            <strong>Reglas del plan:</strong><br/>
                            • El 70% debe estar pagado máximo 60 días antes del evento<br/>
                            • Se divide en máximo 6 cuotas<br/>
                            • Primer pago mínimo: $1.000.000 COP (o equivalente en otra moneda)<br/>
                            • El 30% restante se paga contra entrega
                          </p>
                        </div>
                      )}

                      {/* MODO AUTOMÁTICO SELECCIONADO */}
                      {(quote.modo_plan_pagos === 'automatico' || !puedeUsarRangoFlexible(userRole)) && (
                        <>
                          {/* Encabezado del modo */}
                          {puedeUsarRangoFlexible(userRole) && (
                            <div className="flex items-center justify-between p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Calculator className="w-4 h-4 text-[#D4AF37]" />
                                <span className="text-sm text-[#D4AF37] font-medium">Modo Automático</span>
                              </div>
                              <button
                                onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: '', plan_pagos: [] }))}
                                className="text-xs text-white/50 hover:text-white underline"
                              >
                                Cambiar modo
                              </button>
                            </div>
                          )}

                          {/* Nota informativa */}
                          {puedeUsarRangoFlexible(userRole) && (
                            <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg">
                              <p className="text-xs text-[#D4AF37]/80 leading-relaxed">
                                • Primer pago mínimo: $500.000 - $1.000.000 COP<br/>
                                • Máximo 6 cuotas para el 70%<br/>
                                • 70% pagado 60 días antes del evento
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {/* MODO SEMI-MANUAL SELECCIONADO */}
                      {quote.modo_plan_pagos === 'manual' && puedeUsarRangoFlexible(userRole) && (
                        <>
                          {/* Encabezado del modo */}
                          <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-emerald-400" />
                              <span className="text-sm text-emerald-400 font-medium">Modo Semi-Manual</span>
                            </div>
                            <button
                              onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: '', plan_pagos: [], valor_primera_cuota: 0 }))}
                              className="text-xs text-white/50 hover:text-white underline"
                            >
                              Cambiar modo
                            </button>
                          </div>

                          {/* Campo de primera cuota manual */}
                          <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg space-y-3">
                            <p className="text-xs text-emerald-400/70">
                              ✓ Sin restricciones de mínimos ni máximos
                            </p>
                            <div>
                              <label className="block text-xs text-emerald-400/70 mb-1">
                                Valor de la primera cuota (opcional)
                              </label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400/50 text-sm">
                                  {MONEDAS[quote.moneda]?.simbolo || '$'}
                                </span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1000"
                                  value={quote.valor_primera_cuota || ''}
                                  onChange={(e) => setQuote(prev => ({ 
                                    ...prev, 
                                    valor_primera_cuota: Number(e.target.value) || 0,
                                    plan_pagos: []
                                  }))}
                                  placeholder="Dejar vacío para distribuir equitativamente"
                                  className="w-full px-3 py-2 pl-10 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-400 placeholder-emerald-500/30 focus:outline-none focus:border-emerald-400"
                                />
                              </div>
                              <p className="text-[10px] text-emerald-400/50 mt-1">
                                Si ingresa un valor, el resto se distribuye entre las cuotas restantes.
                              </p>
                            </div>
                          </div>
                        </>
                      )}

                      {/* MODO MANUAL COMPLETO SELECCIONADO */}
                      {quote.modo_plan_pagos === 'manual_completo' && puedeUsarRangoFlexible(userRole) && (
                        <>
                          {/* Encabezado del modo */}
                          <div className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-purple-400" />
                              <span className="text-sm text-purple-400 font-medium">Modo Manual Completo</span>
                            </div>
                            <button
                              onClick={() => setQuote(prev => ({ ...prev, modo_plan_pagos: '', plan_pagos: [], cuotas_manuales: [] }))}
                              className="text-xs text-white/50 hover:text-white underline"
                            >
                              Cambiar modo
                            </button>
                          </div>

                          {/* Información */}
                          <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                            <p className="text-xs text-purple-400/70">
                              ✓ Define cada cuota con su valor y fecha de vencimiento
                            </p>
                          </div>

                          {/* Lista de cuotas manuales */}
                          <div className="space-y-3">
                            {(quote.cuotas_manuales || []).map((cuota, index) => (
                              <div key={`cuota-manual-${index}`} className="p-3 bg-white/5 border border-white/10 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-white/70 font-medium">Cuota {index + 1}</span>
                                  <button
                                    onClick={() => {
                                      const nuevasCuotas = [...(quote.cuotas_manuales || [])];
                                      nuevasCuotas.splice(index, 1);
                                      setQuote(prev => ({ ...prev, cuotas_manuales: nuevasCuotas, plan_pagos: [] }));
                                    }}
                                    className="text-xs text-red-400 hover:text-red-300"
                                  >
                                    Eliminar
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-[10px] text-white/50 mb-1">Valor</label>
                                    <div className="relative">
                                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30 text-xs">
                                        {MONEDAS[quote.moneda]?.simbolo || '$'}
                                      </span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1000"
                                        value={cuota.valor || ''}
                                        onChange={(e) => {
                                          const nuevasCuotas = [...(quote.cuotas_manuales || [])];
                                          nuevasCuotas[index] = { ...nuevasCuotas[index], valor: Number(e.target.value) || 0 };
                                          setQuote(prev => ({ ...prev, cuotas_manuales: nuevasCuotas, plan_pagos: [] }));
                                        }}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 pl-6 bg-white/10 border border-white/20 rounded text-xs text-white placeholder-white/30 focus:outline-none focus:border-purple-400"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-white/50 mb-1">Fecha</label>
                                    <input
                                      type="date"
                                      value={cuota.fecha || ''}
                                      onChange={(e) => {
                                        const nuevasCuotas = [...(quote.cuotas_manuales || [])];
                                        nuevasCuotas[index] = { ...nuevasCuotas[index], fecha: e.target.value };
                                        setQuote(prev => ({ ...prev, cuotas_manuales: nuevasCuotas, plan_pagos: [] }));
                                      }}
                                      className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded text-xs text-white focus:outline-none focus:border-purple-400"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Botón agregar cuota */}
                            <button
                              onClick={() => {
                                const nuevaCuota = { valor: 0, fecha: '' };
                                setQuote(prev => ({ 
                                  ...prev, 
                                  cuotas_manuales: [...(prev.cuotas_manuales || []), nuevaCuota],
                                  plan_pagos: []
                                }));
                              }}
                              className="w-full px-3 py-2 bg-purple-500/10 border border-purple-500/30 border-dashed rounded-lg text-xs text-purple-400 hover:bg-purple-500/20 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>+</span> Agregar Cuota
                            </button>

                            {/* Resumen de cuotas manuales */}
                            {(quote.cuotas_manuales || []).length > 0 && (
                              <div className="p-2 bg-purple-500/10 rounded-lg">
                                <div className="flex justify-between text-xs">
                                  <span className="text-purple-400/70">Total de cuotas:</span>
                                  <span className="text-purple-400">{(quote.cuotas_manuales || []).length}</span>
                                </div>
                                <div className="flex justify-between text-xs mt-1">
                                  <span className="text-purple-400/70">Suma de valores:</span>
                                  <span className="text-purple-400">
                                    {fp((quote.cuotas_manuales || []).reduce((sum, c) => sum + (c.valor || 0), 0))}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* CAMPOS COMUNES - Solo para modos automático y semi-manual */}
                      {((quote.modo_plan_pagos && quote.modo_plan_pagos !== 'manual_completo') || !puedeUsarRangoFlexible(userRole)) && (
                        <>
                          {/* Fecha del evento */}
                          <div>
                            <label className="text-xs text-white/50 block mb-1">Fecha del Evento *</label>
                            <div className="flex items-center gap-2">
                              <CalendarDays className="w-4 h-4 text-white/50" />
                              <input
                                type="date"
                                value={quote.fecha_evento || ''}
                                onChange={(e) => setQuote(prev => ({ ...prev, fecha_evento: e.target.value, plan_pagos: [] }))}
                                className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:border-[#D4AF37] text-sm"
                              />
                            </div>
                            {quote.fecha_evento && (
                              <p className="text-[10px] text-white/40 mt-1">
                                Fecha límite para 70%: {formatDateDisplay(calcularFechaLimite(quote.fecha_evento)?.toISOString().split('T')[0])}
                              </p>
                            )}
                          </div>

                          {/* Configuración del plan - Solo número de cuotas (máx 6) */}
                          <div>
                            <label className="text-xs text-white/50 block mb-1">N° de Cuotas para el 70%</label>
                            <select
                              value={quote.numero_pagos}
                              onChange={(e) => setQuote(prev => ({ ...prev, numero_pagos: parseInt(e.target.value), plan_pagos: [] }))}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:border-[#D4AF37] text-sm"
                            >
                              <option value="1">1 cuota</option>
                              <option value="2">2 cuotas</option>
                              <option value="3">3 cuotas</option>
                              <option value="4">4 cuotas</option>
                              <option value="5">5 cuotas</option>
                              <option value="6">6 cuotas</option>
                            </select>
                          </div>

                          {/* Campo de Anticipo Previo */}
                          <div>
                            <label className="block text-xs text-white/70 mb-1">
                              Anticipo previo (pago ya realizado)
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">
                                {MONEDAS[quote.moneda]?.simbolo || '$'}
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="1000"
                                value={quote.anticipo_previo || ''}
                                onChange={(e) => setQuote(prev => ({ 
                                  ...prev, 
                                  anticipo_previo: Number(e.target.value) || 0,
                                  plan_pagos: []
                                }))}
                                placeholder="0"
                                className="w-full px-3 py-2 pl-10 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                              />
                            </div>
                            <p className="text-[10px] text-white/40 mt-1">
                              Si el cliente ya realizó un pago, ingréselo aquí para descontarlo del plan.
                            </p>
                          </div>
                        </>
                      )}

                      {/* Resumen de valores - Solo para modos que lo necesitan */}
                      {total > 0 && quote.modo_plan_pagos !== 'manual_completo' && (
                        <div className="p-3 bg-white/5 rounded-lg space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-white/50">Total cotización:</span>
                            <span className="text-white">{fp(total)}</span>
                          </div>
                          {quote.anticipo_previo > 0 && (
                            <div className="flex justify-between text-xs text-emerald-400">
                              <span>(-) Anticipo previo:</span>
                              <span>-{fp(quote.anticipo_previo)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs border-t border-white/10 pt-1">
                            <span className="text-white/50">70% a financiar:</span>
                            <span className="text-white">{fp(Math.max(0, Math.round(total * 0.7) - (quote.anticipo_previo || 0)))}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-white/50">30% contra entrega:</span>
                            <span className="text-white">{fp(total - Math.round(total * 0.7))}</span>
                          </div>
                          <div className="flex justify-between text-xs border-t border-white/10 pt-1 font-medium">
                            <span className="text-white/70">Pendiente por pagar:</span>
                            <span className="text-[#D4AF37]">{fp(Math.max(0, total - (quote.anticipo_previo || 0)))}</span>
                          </div>
                        </div>
                      )}

                      {/* Botón generar plan - Solo para modos automático y semi-manual */}
                      {quote.modo_plan_pagos !== 'manual_completo' && (
                        <button
                          onClick={generatePlanPagos}
                          disabled={!quote.fecha_evento || total <= 0}
                          className="w-full px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Generar Plan de Pagos
                        </button>
                      )}

                      {/* Botón aplicar plan manual completo */}
                      {quote.modo_plan_pagos === 'manual_completo' && (
                        <button
                          onClick={() => {
                            const cuotas = quote.cuotas_manuales || [];
                            if (cuotas.length === 0) {
                              toast.error('Debe agregar al menos una cuota');
                              return;
                            }
                            
                            // Validar que todas las cuotas tengan valor y fecha
                            const cuotasInvalidas = cuotas.filter(c => !c.valor || c.valor <= 0 || !c.fecha);
                            if (cuotasInvalidas.length > 0) {
                              toast.error('Todas las cuotas deben tener valor y fecha');
                              return;
                            }

                            // Convertir cuotas manuales a formato de plan_pagos
                            const planPagos = cuotas.map((cuota, index) => ({
                              numero: index + 1,
                              fecha: cuota.fecha,
                              valor: cuota.valor,
                              estado: 'pendiente'
                            }));

                            setQuote(prev => ({ ...prev, plan_pagos: planPagos }));
                            toast.success(`Plan manual aplicado: ${cuotas.length} cuotas`);
                          }}
                          disabled={(quote.cuotas_manuales || []).length === 0}
                          className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Aplicar Plan Manual
                        </button>
                      )}

                      {/* Alerta si hay fechas que exceden el límite */}
                      {fechasConProblema && (
                        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-300">
                            Algunas cuotas exceden la fecha límite (60 días antes del evento). Ajuste las fechas.
                          </p>
                        </div>
                      )}

                      {/* Errores de validación */}
                      {quote.plan_pagos?.length > 0 && (() => {
                        const validation = validarPlanPagos();
                        if (!validation.valid) {
                          return (
                            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                              <p className="text-xs text-red-300 font-medium mb-1">Errores en el plan:</p>
                              <ul className="text-xs text-red-300 space-y-1">
                                {validation.errors.map((err, idx) => (
                                  <li key={`validation-error-${idx}-${err.substring(0, 10)}`}>• {err}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Tabla del plan de pagos */}
                      {quote.plan_pagos?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs text-white/50 uppercase tracking-wider">Plan de Pagos Generado</p>
                          <div className="bg-white/5 rounded-lg overflow-hidden">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-white/10">
                                  <th className="px-2 py-2 text-left text-white/70">#</th>
                                  <th className="px-2 py-2 text-left text-white/70">Fecha</th>
                                  <th className="px-2 py-2 text-left text-white/70">Concepto</th>
                                  <th className="px-2 py-2 text-right text-white/70">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {quote.plan_pagos.map((pago, idx) => {
                                  const esPagoFinal = idx === quote.plan_pagos.length - 1;
                                  return (
                                    <tr key={`pago-${pago.numero}-${idx}`} className={`border-t border-white/10 ${esPagoFinal ? 'bg-white/5' : ''}`}>
                                      <td className="px-2 py-2 text-white/50">{pago.numero}</td>
                                      <td className="px-2 py-2">
                                        <input
                                          type="date"
                                          value={pago.fecha || ''}
                                          onChange={(e) => {
                                            const newPagos = [...quote.plan_pagos];
                                            newPagos[idx] = { ...pago, fecha: e.target.value };
                                            setQuote(prev => ({ ...prev, plan_pagos: newPagos }));
                                          }}
                                          className="w-full px-1 py-1 bg-transparent border-b border-white/20 text-white focus:outline-none focus:border-[#D4AF37] text-xs"
                                        />
                                      </td>
                                      <td className="px-2 py-2 text-white/70 text-[10px]">
                                        {pago.concepto}
                                      </td>
                                      <td className="px-2 py-2 text-right text-[#D4AF37]">
                                        {fp(pago.valor)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-white/10 font-medium">
                                  <td colSpan="3" className="px-2 py-2 text-right text-white">Total:</td>
                                  <td className="px-2 py-2 text-right text-[#D4AF37]">{fp(total)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info de cotización */}
            {isEditing && quote.numero && (
              <div className="bg-zinc-50 rounded-xl p-6">
                <h3 className="font-display text-lg mb-4">Información</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">N° Cotización:</span>
                    <span className="font-medium">{quote.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Creada:</span>
                    <span>{quote.fecha_creacion?.substring(0, 10)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Vence:</span>
                    <span>{quote.fecha_vencimiento?.substring(0, 10)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Creada por:</span>
                    <span>{quote.created_by_name}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal de Vista Previa del PDF */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="relative w-full h-full max-w-5xl max-h-[90vh] m-4 bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col">
            {/* Header del modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-zinc-50">
              <div>
                <h3 className="font-display text-lg">Vista Previa de Cotización</h3>
                <p className="text-sm text-muted-foreground">{quote.numero} - {quote.cliente_nombre}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Descargar
                </button>
                <button
                  onClick={handleClosePreview}
                  className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Contenido del PDF */}
            <div className="flex-1 overflow-hidden bg-zinc-100">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Cargando vista previa...</span>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl + '#toolbar=1&navpanes=0'}
                  className="w-full h-full border-0"
                  title="Vista previa de cotización"
                  type="application/pdf"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p>No se pudo cargar la vista previa</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de cliente encontrado - Mejorado con historial */}
      {showClienteModal && clienteEncontrado?.cliente && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <User className="w-5 h-5 text-amber-600" />
                Cliente Ya Registrado
              </h3>
              <button 
                onClick={() => setShowClienteModal(false)}
                className="text-muted-foreground hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Alerta de coincidencia */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">{clienteEncontrado.cliente.nombre}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {clienteEncontrado.cliente.numero_documento && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {clienteEncontrado.cliente.tipo_documento || 'CC'}: {clienteEncontrado.cliente.numero_documento}
                        </span>
                      )}
                      {clienteEncontrado.cliente.telefono && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          Tel: {clienteEncontrado.cliente.telefono}
                        </span>
                      )}
                      {clienteEncontrado.cliente.correo && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {clienteEncontrado.cliente.correo}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      Coincide por: <strong>{clienteEncontrado.coincide_por?.join(', ')}</strong>
                    </p>
                    {clienteEncontrado.cliente.id_cliente && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Código: {clienteEncontrado.cliente.id_cliente}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Estadísticas del cliente */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600">{clienteEncontrado.estadisticas?.total_fichas || 0}</p>
                  <p className="text-xs text-muted-foreground">Fichas</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{clienteEncontrado.estadisticas?.total_cotizaciones || 0}</p>
                  <p className="text-xs text-muted-foreground">Cotizaciones</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{clienteEncontrado.estadisticas?.total_ventas || 0}</p>
                  <p className="text-xs text-muted-foreground">Ventas</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-600">
                    ${((clienteEncontrado.estadisticas?.valor_total_vendido || 0) / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-muted-foreground">Valor Vendido</p>
                </div>
              </div>
              
              {/* Última cotización */}
              {clienteEncontrado.ultima_cotizacion && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 mb-1 font-medium">Última cotización:</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {clienteEncontrado.ultima_cotizacion.numero}
                      </p>
                      <p className="text-xs text-blue-600">
                        Estado: <span className="capitalize">{clienteEncontrado.ultima_cotizacion.estado}</span>
                        {clienteEncontrado.ultima_cotizacion.fecha_evento && (
                          <> · Evento: {new Date(clienteEncontrado.ultima_cotizacion.fecha_evento).toLocaleDateString('es-CO')}</>
                        )}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-blue-900">
                      ${clienteEncontrado.ultima_cotizacion.total?.toLocaleString('es-CO')}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Referencias de tela de cotizaciones anteriores */}
              {clienteEncontrado.referencias_tela?.length > 0 && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-700 mb-2 font-medium flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    Telas cotizadas anteriormente:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {clienteEncontrado.referencias_tela.slice(0, 6).map((ref) => (
                      <span key={ref} className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                        {ref}
                      </span>
                    ))}
                    {clienteEncontrado.referencias_tela.length > 6 && (
                      <span className="text-xs text-purple-600">
                        +{clienteEncontrado.referencias_tela.length - 6} más
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Historial de cotizaciones */}
              {clienteEncontrado.cotizaciones?.length > 1 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Historial de cotizaciones:</p>
                  <div className="max-h-28 overflow-y-auto space-y-1">
                    {clienteEncontrado.cotizaciones.slice(0, 5).map((cot) => (
                      <div key={cot.numero || cot.id} className="flex items-center justify-between text-xs p-2 bg-zinc-50 rounded">
                        <span className="font-medium">{cot.numero}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                          cot.estado === 'aprobada' || cot.es_venta 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : cot.estado === 'enviada'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-zinc-200 text-zinc-700'
                        }`}>
                          {cot.es_venta ? 'VENTA' : cot.estado}
                        </span>
                        <span className="text-muted-foreground">
                          ${cot.total?.toLocaleString('es-CO')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Mensaje de acción */}
              <div className="bg-zinc-100 rounded-lg p-3 mb-4">
                <p className="text-sm text-zinc-700">
                  <strong>¿Desea asociar esta cotización al cliente existente?</strong>
                </p>
                <p className="text-xs text-zinc-600 mt-1">
                  Se cargarán automáticamente sus datos de contacto y fecha de evento.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={continuarSinAsociar}
                  className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm hover:bg-zinc-50 transition-colors"
                >
                  No asociar
                </button>
                <button
                  onClick={asociarClienteExistente}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors font-medium"
                  data-testid="asociar-cliente-btn"
                >
                  Sí, asociar cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Puntos JPM */}
      {showPuntosModal && clientePuntos && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                Aplicar Puntos JPM
              </h3>
              <button 
                onClick={() => setShowPuntosModal(false)}
                className="text-muted-foreground hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Saldo disponible */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-900">{clientePuntos.puntos_disponibles}</p>
                    <p className="text-sm text-amber-700">Puntos disponibles</p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  Equivalen a ${clientePuntos.valor_redencion?.toLocaleString('es-CO')} COP de beneficio
                </p>
              </div>
              
              {/* Input de puntos a redimir */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  ¿Cuántos puntos desea aplicar?
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={puntosARedimir}
                    onChange={(e) => {
                      const valor = Math.max(0, Math.min(clientePuntos.puntos_disponibles, parseInt(e.target.value) || 0));
                      setPuntosARedimir(valor);
                    }}
                    min="0"
                    max={clientePuntos.puntos_disponibles}
                    className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-[#D4AF37] text-lg font-medium"
                  />
                  <button
                    type="button"
                    onClick={() => setPuntosARedimir(clientePuntos.puntos_disponibles)}
                    className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-sm font-medium transition-colors"
                  >
                    Usar todos
                  </button>
                </div>
                {puntosARedimir > 0 && (
                  <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    Beneficio: ${(puntosARedimir * 100).toLocaleString('es-CO')} COP de descuento
                  </p>
                )}
              </div>
              
              {/* Aviso importante */}
              <div className="bg-zinc-50 rounded-lg p-3 mb-6">
                <p className="text-xs text-zinc-600">
                  <strong>Importante:</strong> Los puntos se reservarán para esta cotización. 
                  El descuento se aplicará definitivamente cuando la venta sea confirmada.
                </p>
              </div>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowPuntosModal(false)}
                  className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-zinc-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={aplicarPuntosJPM}
                  disabled={puntosARedimir <= 0}
                  className="flex-1 px-4 py-3 bg-[#D4AF37] hover:bg-[#C49F27] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Aplicar {puntosARedimir > 0 ? `${puntosARedimir} puntos` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteForm;
